import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { Org } from '@mend/types/org';
import type { User } from '@mend/types/user';
import type { ButtonProps, ChipProps } from '@mui/material';
import type {
  DataGridProProps,
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import type {
  AutomaticOutreachInteractionMode,
  AutomaticOutreachWorkflow,
  CreateOutreachRunResponse,
  FeedFilters,
  ListOfferableSlotsResponse,
  MiaMessageChannel,
  OfferableSlot,
  OfferSlotToWaitlistRequest,
  OutreachProjection,
  OutreachRunSummary,
  OutreachTargetPatch,
  OutreachWorkflow,
  PreviewMessageContentResponse,
  RebookingCandidateStatus,
  MiaOrgConfig,
  RebookingEventKind,
  RiskModelExplainer,
  UnconfirmedRow,
  UnconfirmedRowStatus,
  PatientRiskFactors,
  SendPreparedRequest,
  SendPreparedResponse,
  SendRemindersRequest,
  SetAutomaticOutreachConfigRequest,
  SetReadinessConfigRequest,
} from '#services/api/mia.ts';
import type {
  PatientReadinessDetailsResponse,
  RescheduleRow,
} from '#services/api/patient-readiness.ts';
import type { GetMeResponsePayload } from '#services/api/user.ts';

import * as React from 'react';
import {
  faBell,
  faBolt,
  faCalendarPlus,
  faChartLineUp,
  faCheck,
  faCircleQuestion,
  faPaperPlane,
  faMessageBot,
  faPaperPlaneTop,
  faSliders,
  faSparkles,
  faTriangleExclamation,
  faUserGroup,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCircleCheck as faCircleCheckSolid,
  faUserGroup as faUserGroupSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Link,
  RadioGroup,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, differenceInYears, format, isSameDay, parseISO } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

import { config } from '#config.ts';
import { useLocalState } from '#hooks/local-state.ts';
import { trackPendoEvent } from '#lib/pendo.ts';
import {
  automaticOutreachConfig,
  activeWaitlist,
  miaMessagePreview,
  offerableSlots,
  outreachRun,
  patientReadinessDetails,
  readinessConfig,
  riskModelExplainer,
} from '#lib/react-query/queries.ts';
import {
  approveSlotFill,
  askAboutRebooking,
  canStartOutreach,
  getClaimActionErrorMessage,
  getCouldntBookMessage,
  getOfferSlotErrorMessage,
  isTerminalRunStatus,
  offerSlotToWaitlist,
  releaseSlotFillClaim,
  sendPrepared,
  sendReminders,
  setAutomaticOutreachConfig,
  setReadinessConfig,
  streamOutreachTargetPatches,
} from '#services/api/mia.ts';
import {
  getUnconfirmedRowRiskProbability,
  getUnconfirmedRowRiskSortValue,
} from '#services/api/patient-readiness.ts';
import { MiaUnauthorizedError } from '#services/mia-session.ts';
import { formatPhoneNumber } from '#utils/phone.ts';
import { canEditReadinessThreshold, canManageAutomaticOutreach } from '#utils/roles.ts';
import * as DataAgent from '../DataAgent.tsx';
import { ActiveFiltersBadge } from './ActiveFiltersBadge.tsx';
import * as BriefingCard from './BriefingCard.tsx';
import * as OutreachDialog from './OutreachDialog.tsx';
import {
  attendanceRiskCopy,
  createConfirmationLeadTimeFilter,
  deriveFallbackThreshold,
  fallbackFlaggedShare,
  formatReadinessDateTime,
  pluralize,
  readinessDateTimeFormat,
  selectFlaggedRows,
} from './Readiness.utils.ts';
import * as ReadinessBucketCard from './ReadinessBucketCard.tsx';
import { RebookDialog } from './RebookDialog.tsx';
import { RequestConfirmationDialog } from './RequestConfirmationDialog.tsx';
import { RiskModelDialog, type ExplainerState, type ThresholdState } from './RiskModelDialog.tsx';
import { WaitlistDialog } from './WaitlistDialog.tsx';

const readinessBuckets = ['unconfirmed', 'reschedule', 'waitlist'] as const;
type ReadinessBucket = (typeof readinessBuckets)[number];
const readinessScrollGapSpacing = 2;

type DataAgentStatus = 'auto-pending' | 'auto-ready' | 'auto-viewed';

interface BriefingOutreachResult {
  // The filters the outreach was started under: a run describes one slice, and its banner
  // must not survive into a different one.
  filtersKey: string;
  // The run this banner is reporting on. Held so the banner can ask whether the run has finished —
  // without it the card has a start state and no end state, and reads "processing" forever.
  runId: number;
}

const unconfirmedBucket = {
  key: 'unconfirmed',
  color: 'error',
  // Mirrors `unconfirmedWorklistDaysAhead` in patient-readiness.ts — change them together.
  emphasis: 'Next 2 days',
} as const;

const waitlistBucket = {
  key: 'waitlist',
  color: 'quaternary',
  metricLabel: 'Appointment Slots to Fill',
  emphasis: 'Due to late cancellation',
} as const;

const rescheduleBucket = {
  key: 'reschedule',
  color: 'warning',
  metricLabel: 'Patients Who Need to Rebook',
  emphasis: 'Last 7 days',
} as const;

const automaticOutreachWorkflowPresentation = {
  confirmation: {
    label: 'confirmation',
    getScheduleDescription: (localTime: string) =>
      `Automatic confirmation outreach will begin tomorrow at ${localTime} in your practice's local time.`,
  },
  rebooking: {
    label: 'rebooking',
    getScheduleDescription: (localTime: string) =>
      `Automatic rebooking outreach runs daily at ${localTime} in your practice's local time.`,
  },
} satisfies Record<
  AutomaticOutreachWorkflow,
  { label: string; getScheduleDescription: (localTime: string) => string }
>;

const interactionModeLabels: Record<AutomaticOutreachInteractionMode, string> = {
  confirm_by_link: 'Confirm by Link',
  call_or_join_waitlist: 'Call or Join Waitlist',
};

/**
 * Narrows the explainer query to what the modal renders. A settled query with no data is a failed
 * read, which the modal must report as an outage rather than as a fact about the practice.
 */
function toExplainerState(query: {
  data: RiskModelExplainer | undefined;
  isPending: boolean;
}): ExplainerState {
  if (query.isPending) {
    return { status: 'loading' };
  }

  return query.data ? { status: 'ready', explainer: query.data } : { status: 'failed' };
}

/**
 * Resolves the threshold the briefing and modal both work from.
 *
 * A `saved` or `recommended` threshold is a real per-org number and is used exactly as given. Only
 * the global `fallback` is replaced — by a floor read off this org's own loaded predictions — and
 * only when there is a distribution to read it from; an org with nothing scored keeps the server's
 * constant, which no briefing state consults anyway.
 */
function toThresholdState(
  query: { data: MiaOrgConfig | undefined; isPending: boolean },
  atRiskRows: UnconfirmedRow[]
): ThresholdState {
  if (query.isPending) {
    return { status: 'loading' };
  }

  if (!query.data) {
    return { status: 'failed' };
  }

  const { riskThreshold, thresholdSource } = query.data;
  const derived =
    thresholdSource === 'fallback'
      ? deriveFallbackThreshold(atRiskRows, fallbackFlaggedShare)
      : null;

  return derived === null
    ? { status: 'ready', threshold: riskThreshold, basis: thresholdSource }
    : { status: 'ready', threshold: derived, basis: 'derived' };
}

function isReadinessBucket(bucket: string | null): bucket is ReadinessBucket {
  return (readinessBuckets as readonly string[]).includes(bucket ?? '');
}

function getSelectedBucket(bucket: string | null): ReadinessBucket {
  return isReadinessBucket(bucket) ? bucket : 'unconfirmed';
}

// TODO(multi-channel): deliberately SMS-gated. The backend predicts email without considering
// MIA_EMAIL_ENABLED, but execution rejects email while that flag is off. Once channel execution
// availability is exposed to this client, broaden this predicate and update the SMS-only dialog
// copy and single-send previews with it.
type SmsReachableRow<TRow extends { preferredChannel: MiaMessageChannel | null }> = TRow & {
  preferredChannel: 'sms';
};

function isSmsReachableUnconfirmedRow(row: UnconfirmedRow): row is SmsReachableRow<UnconfirmedRow> {
  return (
    row.status === 'not_contacted' &&
    row.preferredChannel === 'sms' &&
    canStartOutreach(row.outreach)
  );
}

function isSmsReachableRebookingRow(row: RescheduleRow): row is SmsReachableRow<RescheduleRow> {
  return (
    row.status === 'needs_outreach' &&
    row.preferredChannel === 'sms' &&
    canStartOutreach(row.outreach)
  );
}

function countRebookingPatients(rows: RescheduleRow[]): number {
  return new Set(rows.map((row) => row.patient.userId)).size;
}

interface RebookingOutreachSelection {
  rows: SmsReachableRow<RescheduleRow>[];
  recipientCount: number;
  excludedPatientCount: number;
  excludedAppointmentCount: number;
}

function selectReadinessDetails(data: PatientReadinessDetailsResponse) {
  const actionableRescheduleRows = data.reschedule.filter(isSmsReachableRebookingRow);
  const reschedulePatientCount = countRebookingPatients(data.reschedule);
  const recipientCount = countRebookingPatients(actionableRescheduleRows);
  const rescheduleOutreach: RebookingOutreachSelection = {
    rows: actionableRescheduleRows,
    recipientCount,
    // A patient is excluded only when none of their appointments can receive outreach.
    excludedPatientCount: reschedulePatientCount - recipientCount,
    excludedAppointmentCount: data.reschedule.length - actionableRescheduleRows.length,
  };

  return {
    ...data,
    notContactedUnconfirmedRows: data.unconfirmed.filter(isSmsReachableUnconfirmedRow),
    reschedulePatientCount,
    rescheduleOutreach,
  };
}

// Every consumer — the tab's read, invalidations, and single-send row patches — must target the
// active query exactly, filters included, or a mutation would update a cache entry nothing is
// rendering. These helpers keep the key construction in one place.
function activeReadinessQuery(orgId: number, filters: FeedFilters) {
  return patientReadinessDetails({ orgId, ...filters });
}

function activeOfferableSlotsQuery(orgId: number, filters: FeedFilters) {
  return offerableSlots({ orgId, ...filters });
}

/**
 * Why the briefing can't rank what's at risk. Both cases leave a real backlog unranked, so they read
 * as the same shape of briefing — but never as the same sentence, because "your practice has no
 * predictions yet" and "I couldn't read your threshold" are not the same admission.
 */
type UnrankableReason = 'no-predictions' | 'threshold-unavailable';

/**
 * What an unrankable briefing says, split so the standing offer of outreach can be dropped on its
 * own. That offer only holds while something is left to send — a briefing that promises outreach it
 * has no one to send to makes a claim the screen can't honour.
 */
const unrankableCopy = {
  'no-predictions': {
    admission: "I'll start flagging risk here once predictions are available for your practice.",
    standingOffer: 'Until then the outreach is ready whenever you are.',
  },
  'threshold-unavailable': {
    admission: "I couldn't load your practice's risk threshold, so I'm not ranking these yet.",
    standingOffer: 'The outreach is ready whenever you are.',
  },
} as const satisfies Record<UnrankableReason, { admission: string; standingOffer: string }>;

function getUnrankableCopy(unrankable: {
  reason: UnrankableReason;
  hasOutreachToSend: boolean;
}): string {
  const { admission, standingOffer } = unrankableCopy[unrankable.reason];

  return unrankable.hasOutreachToSend ? `${admission} ${standingOffer}` : admission;
}

/**
 * What the briefing says once every appointment it flagged is already awaiting a reply. Drops the
 * count at one, where "to all 1" reads as a miscount — the same fork the dialog title and confirm
 * label already make.
 */
function getFlaggedAllContactedCopy(flaggedCount: number): string {
  const reachedOut =
    flaggedCount === 1
      ? "I've already reached out."
      : `I've already reached out to all ${flaggedCount}.`;

  return `${reachedOut} I'm waiting for replies and I'll update you the moment anything needs you.`;
}

/**
 * Which of the five things the briefing has to say. Ordered by precedence: a send this session is
 * the freshest news, and "I can't rank these" outranks the ranking states because ranking is what it
 * lacks. `unrankable` is deliberately distinct from `all-clear` — an org with unconfirmed
 * appointments it couldn't rank has plenty at risk, so telling it things look good would be a lie.
 *
 * The two ranked states need no precedence ruling between them: one requires a non-empty sendable
 * slice and the other an empty one, so they are mutually exclusive by construction.
 *
 * Known gap, accepted (BAD-603): the lead-time floor narrows the flagged set, so a practice whose
 * every flagged appointment is imminent flags nothing and lands here on `all-clear` — "things are
 * looking good" over high-risk imminent appointments. Taken over adding copy for it; pinned by a
 * test.
 */
type BriefingState =
  | { type: 'outreach-started'; outreachResult: BriefingOutreachResult }
  | { type: 'unrankable'; reason: UnrankableReason; hasOutreachToSend: boolean }
  | { type: 'appointments-at-risk' }
  | { type: 'flagged-all-contacted' }
  | { type: 'all-clear' };

/**
 * `scoredCount` and `flaggedCount` are separate inputs on purpose. Under a threshold, "nothing
 * flagged" no longer implies "nothing scored": a practice whose every prediction sits below its own
 * threshold is genuinely all-clear, and must not be told its predictions haven't arrived. (The
 * relative-share ranking this replaced could infer one from the other, because its floor of one
 * guaranteed a flag whenever anything was scored.)
 *
 * `sendableCount` is separate again, because a Flagged Appointment stays flagged once its outreach
 * has gone out — the risk it names hasn't resolved. So it decides which control the briefing offers,
 * never what it counts.
 */
function getBriefingState(params: {
  outreachResult: BriefingOutreachResult | null;
  atRiskCount: number;
  scoredCount: number;
  flaggedCount: number;
  sendableCount: number;
  isThresholdResolved: boolean;
}): BriefingState {
  if (params.outreachResult) {
    return { type: 'outreach-started', outreachResult: params.outreachResult } as const;
  }

  const hasOutreachToSend = params.sendableCount > 0;

  // Both unrankable cases are gated on there being something to rank: with nothing at risk, an
  // unknown threshold changes nothing and all-clear is the honest answer.
  if (params.atRiskCount > 0) {
    if (!params.isThresholdResolved) {
      return { type: 'unrankable', reason: 'threshold-unavailable', hasOutreachToSend } as const;
    }

    if (params.scoredCount === 0) {
      return { type: 'unrankable', reason: 'no-predictions', hasOutreachToSend } as const;
    }
  }

  if (params.flaggedCount > 0) {
    return hasOutreachToSend
      ? ({ type: 'appointments-at-risk' } as const)
      : ({ type: 'flagged-all-contacted' } as const);
  }

  return { type: 'all-clear' } as const;
}

/**
 * The briefing's send entry point, rendered only where it has appointments to send to. A briefing
 * with nothing left says so; it never offers a control that can't act.
 */
function ReviewOutreachButton(props: { onClick: () => void }) {
  return (
    <Button
      variant="contained"
      color="quaternary"
      size="large"
      startIcon={<FontAwesomeScalableIcon icon={faPaperPlaneTop} />}
      onClick={props.onClick}
    >
      Review the outreach
    </Button>
  );
}

/**
 * Asks the assistant about the ranking on the reader's behalf. Only offered while there is outreach
 * still to send, which is the moment the ranking is what they're about to act on; once it has gone
 * out, {@link AskCoordinateButton} takes this slot instead.
 */
function ExplainFlaggedAppointmentsButton(props: { flaggedCount: number; onClick: () => void }) {
  return (
    <Button
      variant="outlined"
      color="neutral"
      size="large"
      startIcon={<FontAwesomeScalableIcon icon={faCircleQuestion} />}
      onClick={props.onClick}
    >
      {props.flaggedCount === 1 ? 'Why this appointment' : 'Why these appointments'}
    </Button>
  );
}

/**
 * The briefing's assistant entry point wherever the outreach has already gone out — and the only
 * control an unranked practice with nothing left to send has at all. Deliberately generic: it claims
 * nothing is ranked, so it stays honest on a briefing with no ranking to explain, and it opens the
 * assistant with no question and no appointment context attached.
 */
function AskCoordinateButton(props: { onClick: () => void }) {
  return (
    <Button
      variant="outlined"
      color="neutral"
      size="large"
      startIcon={<FontAwesomeScalableIcon icon={faMessageBot} />}
      onClick={props.onClick}
    >
      Ask Coordinate
    </Button>
  );
}

const afternoonStartHour = 12;
const eveningStartHour = 18;

function getTimeOfDayGreeting(date: Date): string {
  const hour = date.getHours();

  if (hour >= eveningStartHour) {
    return 'Good evening';
  }

  if (hour >= afternoonStartHour) {
    return 'Good afternoon';
  }

  return 'Good morning';
}

const outreachStreamReconnectDelayMs = 5_000;
const outreachStreamMaxReconnectDelayMs = 60_000;
const outreachStreamStabilityWindowMs = 30_000;
const outreachStreamMinimumJitterRatio = 0.5;
const outreachTerminalRefetchDelayMs = 250;
const outreachWorkflows = [
  'confirmation',
  'rebooking',
  'slot_fill',
] as const satisfies readonly OutreachWorkflow[];

function toOutreachProjection(patch: OutreachTargetPatch): OutreachProjection {
  return {
    runId: patch.runId,
    trigger: patch.trigger,
    targetId: patch.targetId,
    status: patch.status,
    version: patch.version,
    attemptCounts: patch.attemptCounts,
    unrecordedContactCount: patch.unrecordedContactCount,
    updatedAt: patch.updatedAt,
  };
}

function shouldApplyOutreachPatch(
  current: OutreachProjection | null,
  patch: OutreachTargetPatch
): boolean {
  return current === null || patch.version > current.version;
}

function isTerminalOutreachPatch(patch: OutreachTargetPatch): boolean {
  return !['queued', 'processing', 'retrying'].includes(patch.status);
}

function waitForOutreachStreamReconnect(signal: AbortSignal, attempt: number): Promise<void> {
  if (signal.aborted) {
    return Promise.resolve();
  }

  const cappedDelayMs = Math.min(
    outreachStreamReconnectDelayMs * 2 ** attempt,
    outreachStreamMaxReconnectDelayMs
  );
  const minimumDelayMs = cappedDelayMs * outreachStreamMinimumJitterRatio;
  const delayMs = minimumDelayMs + Math.random() * (cappedDelayMs - minimumDelayMs);

  return new Promise((resolve) => {
    function handleAbort() {
      window.clearTimeout(timer);
      resolve();
    }

    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve();
    }, delayMs);
    signal.addEventListener('abort', handleAbort, { once: true });
  });
}

function useAutomaticOutreachControl(options: {
  orgId: number;
  workflow: AutomaticOutreachWorkflow;
}) {
  const queryClient = useQueryClient();
  const queryOptions = automaticOutreachConfig(options);
  const query = useQuery(queryOptions);
  const workflowLabel = automaticOutreachWorkflowPresentation[options.workflow].label;
  const mutation = useMutation({
    mutationFn: (enabled: boolean) => {
      const request: SetAutomaticOutreachConfigRequest = {
        orgId: options.orgId,
        workflow: options.workflow,
        enabled,
      };
      return setAutomaticOutreachConfig(request);
    },
    onSuccess: async (savedConfig) => {
      await queryClient.cancelQueries({ queryKey: queryOptions.queryKey, exact: true });
      trackPendoEvent('coordinate_automatic_outreach_saved', {
        workflow: options.workflow,
        enabled: savedConfig.enabled,
      });
      queryClient.setQueryData(queryOptions.queryKey, savedConfig);
      enqueueSnackbar(
        savedConfig.enabled
          ? `Automatic ${workflowLabel} outreach is on.`
          : `Automatic ${workflowLabel} outreach is off.`,
        { variant: 'success' }
      );
    },
    onError: () => {
      enqueueSnackbar(
        `Automatic ${workflowLabel} outreach could not be updated. Please try again later.`,
        { variant: 'error' }
      );
    },
  });

  return { mutation, query };
}

export function Readiness(props: { me: GetMeResponsePayload; filters: FeedFilters }) {
  const orgId = props.me.org.id;
  // Home rebuilds the filters object every render; key it by value so downstream memos (the
  // detail grids' column defs, whose renderCell closures carry it) only churn when a filter
  // actually changes — a new columns identity resets the grid's column widths.
  const filtersKey = JSON.stringify(props.filters);
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- the key stands in for the object
  const filters = React.useMemo(() => props.filters, [filtersKey]);

  const query = useQuery({
    ...activeReadinessQuery(orgId, filters),
    select: selectReadinessDetails,
  });
  const offerableSlotsQuery = useQuery(activeOfferableSlotsQuery(orgId, filters));
  // A slot-fill outage never blocks the rest of Readiness.
  const slotFillData = offerableSlotsQuery.isSuccess ? offerableSlotsQuery.data : null;
  const slotFillEnabled = slotFillData?.enabled ?? false;
  const activeWaitlistQuery = useQuery({
    ...activeWaitlist({ orgId }),
    enabled: slotFillEnabled,
  });
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBucket = getSelectedBucket(searchParams.get('bucket'));
  const readinessContainerRef = React.useRef<HTMLDivElement>(null);
  const pendingScrollBucketRef = React.useRef<ReadinessBucket | null>(null);
  React.useEffect(() => {
    if (pendingScrollBucketRef.current !== selectedBucket || !readinessContainerRef.current) {
      return;
    }

    // Wait for the newly mounted grid to measure its rows; otherwise the shorter interim page
    // can cap the scroll before the readiness container reaches the viewport top.
    const frame = window.requestAnimationFrame(() => {
      if (pendingScrollBucketRef.current !== selectedBucket || !readinessContainerRef.current) {
        return;
      }

      pendingScrollBucketRef.current = null;
      const readinessContainer = readinessContainerRef.current;
      const scrollGapPx = Number.parseFloat(
        window.getComputedStyle(readinessContainer).scrollMarginTop
      );
      // Include the landing gap so even a container just below the viewport top never scrolls up.
      if (readinessContainer.getBoundingClientRect().top <= scrollGapPx) {
        return;
      }

      readinessContainer.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedBucket]);
  const riskModelExplainerQuery = useQuery(riskModelExplainer({ orgId }));
  const readinessConfigQueryOptions = readinessConfig({ orgId });
  const readinessConfigQuery = useQuery(readinessConfigQueryOptions);
  const confirmationAutomaticOutreach = useAutomaticOutreachControl({
    orgId,
    workflow: 'confirmation',
  });
  const rebookingAutomaticOutreach = useAutomaticOutreachControl({
    orgId,
    workflow: 'rebooking',
  });
  const automaticOutreachControls = {
    confirmation: confirmationAutomaticOutreach,
    rebooking: rebookingAutomaticOutreach,
  } as const;
  const [enableDialogWorkflow, setEnableDialogWorkflow] =
    React.useState<AutomaticOutreachWorkflow | null>(null);
  const [isWaitlistDialogOpen, setWaitlistDialogOpen] = React.useState(false);
  const [isAtRiskPatientsDrawerOpen, setAtRiskPatientsDrawerOpen] = React.useState(false);
  const [isBriefingOutreachDialogOpen, setBriefingOutreachDialogOpen] = React.useState(false);
  const [isRiskModelDialogOpen, setRiskModelDialogOpen] = React.useState(false);
  const [briefingOutreachResult, setBriefingOutreachResult] =
    React.useState<BriefingOutreachResult | null>(null);
  // The run behind the briefing banner, polled until it settles so the card can stop saying
  // "processing". Declared here with the other hooks rather than beside the banner it feeds: the
  // component returns early while the roster loads, and a hook after that is a hook that sometimes
  // does not run. A failed read leaves `data` undefined, which the card reads as still-running —
  // the honest default, since a run we cannot see the end of is not one we can call finished.
  const briefingRunId =
    briefingOutreachResult?.filtersKey === filtersKey ? briefingOutreachResult.runId : null;
  const briefingRunQuery = useQuery({
    ...outreachRun({ orgId, runId: briefingRunId ?? 0 }),
    enabled: briefingRunId !== null,
  });
  const [greeting] = React.useState(() => getTimeOfDayGreeting(new Date()));
  const atRiskPatientsAgentStatusRef = React.useRef<DataAgentStatus>('auto-pending');
  const tabOpenedAtRef = React.useRef(Date.now());

  React.useEffect(() => {
    const abortController = new AbortController();
    const readinessQueryKey = activeReadinessQuery(orgId, filters).queryKey;
    const offerableSlotsQueryKey = activeOfferableSlotsQuery(orgId, filters).queryKey;
    const terminalRefetchTimers = new Map<OutreachWorkflow, number>();

    function invalidateWorkflow(workflow: OutreachWorkflow): Promise<void> {
      return queryClient.invalidateQueries({
        queryKey: workflow === 'slot_fill' ? offerableSlotsQueryKey : readinessQueryKey,
      });
    }

    function scheduleTerminalRefetch(workflow: OutreachWorkflow) {
      const currentTimer = terminalRefetchTimers.get(workflow);
      if (currentTimer !== undefined) {
        window.clearTimeout(currentTimer);
      }

      terminalRefetchTimers.set(
        workflow,
        window.setTimeout(() => {
          terminalRefetchTimers.delete(workflow);
          void invalidateWorkflow(workflow);
        }, outreachTerminalRefetchDelayMs)
      );
    }

    function applyReadinessPatch(
      workflow: 'confirmation' | 'rebooking',
      patch: OutreachTargetPatch
    ) {
      const currentData =
        queryClient.getQueryData<PatientReadinessDetailsResponse>(readinessQueryKey);
      if (!currentData) {
        return;
      }

      if (workflow === 'confirmation') {
        const row = currentData.unconfirmed.find((item) => item.appointmentId === patch.entityId);
        if (!row || !shouldApplyOutreachPatch(row.outreach, patch)) {
          return;
        }

        queryClient.setQueryData<PatientReadinessDetailsResponse>(readinessQueryKey, {
          ...currentData,
          unconfirmed: currentData.unconfirmed.map((item) =>
            item.appointmentId === patch.entityId
              ? { ...item, outreach: toOutreachProjection(patch) }
              : item
          ),
        });
      } else {
        const row = currentData.reschedule.find((item) => item.id === patch.entityId);
        if (!row || !shouldApplyOutreachPatch(row.outreach, patch)) {
          return;
        }

        queryClient.setQueryData<PatientReadinessDetailsResponse>(readinessQueryKey, {
          ...currentData,
          reschedule: currentData.reschedule.map((item) =>
            item.id === patch.entityId ? { ...item, outreach: toOutreachProjection(patch) } : item
          ),
        });
      }

      if (isTerminalOutreachPatch(patch)) {
        scheduleTerminalRefetch(workflow);
      }
    }

    function applySlotFillPatch(patch: OutreachTargetPatch) {
      const currentData =
        queryClient.getQueryData<ListOfferableSlotsResponse>(offerableSlotsQueryKey);
      const slot = currentData?.slots.find((item) => item.appointmentId === patch.entityId);
      if (!currentData || !slot || !shouldApplyOutreachPatch(slot.outreach, patch)) {
        return;
      }

      queryClient.setQueryData<ListOfferableSlotsResponse>(offerableSlotsQueryKey, {
        ...currentData,
        slots: currentData.slots.map((item) =>
          item.appointmentId === patch.entityId
            ? { ...item, outreach: toOutreachProjection(patch) }
            : item
        ),
      });

      if (isTerminalOutreachPatch(patch)) {
        scheduleTerminalRefetch('slot_fill');
      }
    }

    async function maintainOutreachStream(workflow: OutreachWorkflow): Promise<void> {
      let reconnectAttempt = 0;

      while (!abortController.signal.aborted) {
        const connection: { openedAt: number | null } = { openedAt: null };

        try {
          await streamOutreachTargetPatches({
            workflow,
            orgId,
            signal: abortController.signal,
            onOpen: () => {
              connection.openedAt = Date.now();
              return invalidateWorkflow(workflow);
            },
            onPatch: (patch) => {
              if (workflow === 'slot_fill') {
                applySlotFillPatch(patch);
                return;
              }

              applyReadinessPatch(workflow, patch);
            },
          });
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }

          // Session recovery is already redirecting; reconnecting would repeat the rejected request.
          if (error instanceof MiaUnauthorizedError) {
            return;
          }

          console.warn('[readiness] Outreach stream failed; reconnecting.', { error, workflow });
        }

        const openDurationMs = connection.openedAt === null ? 0 : Date.now() - connection.openedAt;
        if (openDurationMs >= outreachStreamStabilityWindowMs) {
          reconnectAttempt = 0;
        } else if (connection.openedAt !== null) {
          console.warn('[readiness] Outreach stream closed shortly after opening; reconnecting.', {
            openDurationMs,
            workflow,
          });
        }

        await waitForOutreachStreamReconnect(abortController.signal, reconnectAttempt);
        reconnectAttempt += 1;
      }
    }

    for (const workflow of outreachWorkflows) {
      void maintainOutreachStream(workflow);
    }

    return () => {
      abortController.abort();
      for (const timer of terminalRefetchTimers.values()) {
        window.clearTimeout(timer);
      }
    };
  }, [filters, orgId, queryClient]);

  // Only ever fired from the admin-gated Save in the risk-model modal — the server's
  // `orgAdminProcedure` is the second line, not the first.
  const setReadinessConfigMutation = useMutation({
    mutationFn: (request: SetReadinessConfigRequest) => setReadinessConfig(request),
    onSuccess: (savedConfig) => {
      // Captured on success, not on click — a failed POST must not be recorded as a saved
      // threshold — and with the persisted value, which is the fact the event claims.
      trackPendoEvent('coordinate_threshold_saved', {
        threshold: savedConfig.riskThreshold,
      });
      enqueueSnackbar('Your risk threshold is saved.', { variant: 'success' });
      // The response is the authoritative config, so write it in rather than refetching.
      queryClient.setQueryData(readinessConfigQueryOptions.queryKey, savedConfig);
      setRiskModelDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Your risk threshold could not be saved. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function handleAtRiskPatientsAutoDetectResponse() {
    // Track readiness only while the automatic explanation is unseen; opening the drawer marks
    // the session as viewed, and follow-up responses from the same session are ignored.
    if (atRiskPatientsAgentStatusRef.current !== 'auto-pending') {
      return;
    }

    atRiskPatientsAgentStatusRef.current = 'auto-ready';
    trackPendoEvent('coordinate_risk_explanation_ready', {
      ms_since_tab_opened: Date.now() - tabOpenedAtRef.current,
    });
  }

  function closeAtRiskPatientsDrawer() {
    setAtRiskPatientsDrawerOpen(false);
  }

  function selectBucket(bucket: ReadinessBucket) {
    if (bucket === selectedBucket) {
      return;
    }

    trackPendoEvent('coordinate_bucket_selected', { bucket });
    pendingScrollBucketRef.current = bucket;
    setSearchParams((currentSearchParams) => {
      const nextSearchParams = new URLSearchParams(currentSearchParams);
      nextSearchParams.set('bucket', bucket);
      return nextSearchParams;
    });
  }

  // Shared by the unconfirmed card's `Root` (for its interactive descendants) and its card-scoped
  // CTA, so a click on either while the card isn't selected yet selects it the same way.
  function selectUnconfirmedCard() {
    selectBucket(unconfirmedBucket.key);
  }

  function selectRescheduleCard() {
    selectBucket(rescheduleBucket.key);
  }

  function selectWaitlistCard() {
    selectBucket(waitlistBucket.key);
  }

  React.useEffect(() => {
    trackPendoEvent('coordinate_tab_opened');
  }, []);

  // Deliberately not gated on offerableSlotsQuery — Slot-Fill is a bolt-on; the
  // waitlist card starts as its placeholder and fills in when that read lands.
  if (query.isLoading) {
    return (
      <Card sx={{ padding: 2, width: '100%' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <Skeleton variant="rounded" height={180} />
          <Skeleton variant="rounded" height={180} />
          <Skeleton variant="rounded" height={180} />
        </Box>

        <Skeleton variant="rounded" height={200} />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
        Readiness summary could not load. Please try again later.
      </Alert>
    );
  }

  const canEditThreshold = canEditReadinessThreshold(props.me.user.role);
  const canManageScheduledOutreach = canManageAutomaticOutreach(props.me.user.role);
  // Not surfaced as wording (the UI speaks the "attendance risk" umbrella) — it rides along on the
  // modal-opened event so the analytics can tell the two models' audiences apart.
  const riskKind = query.data.riskKind;
  const unconfirmedRows = query.data.unconfirmed;
  // Confirmed rows stay in the grid as outcome context, but ranking is over appointments that could
  // still be missed — a confirmed one needs no outreach however the model scored it.
  const atRiskRows = unconfirmedRows.filter((row) => row.status !== 'confirmed');
  const unconfirmedCount = atRiskRows.length;
  // Every scored row's probability. Feeds the briefing's flagging and — via the modal — the live
  // count its slider previews, so both answer "who clears this threshold" from the same numbers.
  const riskProbabilities = atRiskRows
    .map(getUnconfirmedRowRiskProbability)
    .filter((probability): probability is number => probability !== null);
  const thresholdState = toThresholdState(readinessConfigQuery, atRiskRows);
  const confirmationOutreachConfigUnavailable =
    confirmationAutomaticOutreach.query.isError ||
    (!confirmationAutomaticOutreach.query.isPending && !confirmationAutomaticOutreach.query.data);
  const confirmationOutreachEnabled = confirmationAutomaticOutreach.query.data?.enabled ?? false;
  const confirmationOutreachControlDisabled =
    confirmationAutomaticOutreach.query.isPending ||
    confirmationOutreachConfigUnavailable ||
    !canManageScheduledOutreach ||
    confirmationAutomaticOutreach.mutation.isPending;
  const rebookingOutreachConfigUnavailable =
    rebookingAutomaticOutreach.query.isError ||
    (!rebookingAutomaticOutreach.query.isPending && !rebookingAutomaticOutreach.query.data);
  const rebookingOutreachEnabled = rebookingAutomaticOutreach.query.data?.enabled ?? false;
  const rebookingOutreachControlDisabled =
    rebookingAutomaticOutreach.query.isPending ||
    rebookingOutreachConfigUnavailable ||
    !canManageScheduledOutreach ||
    rebookingAutomaticOutreach.mutation.isPending;
  // Render-time, and deliberately not in a react-query `select`: a `select` is memoized on the data,
  // so a wall-clock comparison there stays stale for a whole polling interval and would offer sends
  // the server already refuses. Also not in `isSmsReachableUnconfirmedRow`, which drives the per-row
  // button — staff who have just spoken to an imminent patient know something Mia doesn't.
  const hasSendableLeadTime = createConfirmationLeadTimeFilter(
    query.data.confirmationLeadTimeFloorMinutes
  );
  // Narrowed before flagging rather than after, which keeps the briefing's headline count and its
  // send the same set — so no state has to explain a shortfall between them. The threshold and
  // `scoredCount` stay over every at-risk row: imminence says nothing about an org's risk
  // distribution, and a practice whose only scored rows are imminent still has predictions.
  const flaggedRows =
    thresholdState.status === 'ready'
      ? selectFlaggedRows(atRiskRows.filter(hasSendableLeadTime), thresholdState.threshold)
      : [];
  const flaggedCount = flaggedRows.length;
  const cardSendableRows = query.data.notContactedUnconfirmedRows.filter(hasSendableLeadTime);
  // Two entry points, two populations — the one surprising thing here. The briefing sends to what it
  // flagged; the card below still reaches every uncontacted appointment, because the threshold
  // decides what Coordinate leads with, not what staff may act on.
  //
  // Nothing flagged widens the briefing to that same full population rather than sending nothing:
  // there is no ranking to defer to. That also covers a degraded prediction read, which is
  // indistinguishable from an unscored practice — the worklist returns nulls rather than failing.
  // Both sides of the fork are already past the floor, so neither needs filtering again.
  const briefingSendableRows =
    flaggedCount > 0 ? flaggedRows.filter(isSmsReachableUnconfirmedRow) : cardSendableRows;
  // A send that happened under different filters is old news for this slice.
  const activeOutreachResult =
    briefingOutreachResult?.filtersKey === filtersKey ? briefingOutreachResult : null;
  const briefingRun = activeOutreachResult ? briefingRunQuery.data : undefined;
  const briefingOutreach = getBriefingOutreachPresentation(briefingRun);
  const briefingState = getBriefingState({
    outreachResult: activeOutreachResult,
    atRiskCount: unconfirmedCount,
    scoredCount: riskProbabilities.length,
    flaggedCount,
    sendableCount: briefingSendableRows.length,
    isThresholdResolved: thresholdState.status === 'ready',
  });
  // Both ranked states keep the at-risk chrome — eyebrow colour and the confirmation-lift aside —
  // and both offer the assistant. Only the one with outreach still to send auto-asks about the
  // ranking; the rest reach the assistant through an open invitation.
  const isBriefingRanked =
    briefingState.type === 'appointments-at-risk' || briefingState.type === 'flagged-all-contacted';
  const hasBriefingAssistant =
    isBriefingRanked || (briefingState.type === 'unrankable' && !briefingState.hasOutreachToSend);
  const rescheduleRows = query.data.reschedule;
  const rescheduleOutreach = query.data.rescheduleOutreach;
  const offerableSlotRows = slotFillData?.slots ?? [];
  const actionableOfferableSlotRows = slotFillData
    ? offerableSlotRows.filter(
        (slot) =>
          !slot.slotFillState &&
          countReachableCandidates([slot], slotFillData.offerBatchSize) > 0 &&
          canStartOutreach(slot.outreach)
      )
    : [];
  const flaggedAppointmentIds = flaggedRows.map((row) => row.appointmentId).join(', ');
  const dataAgentInitialPrompt =
    briefingState.type !== 'appointments-at-risk'
      ? undefined
      : flaggedCount === 1
        ? 'Why is the patient of this appointment at risk?'
        : 'Why are the patients of these appointments at risk?';
  const dataAgentInitialPrivateContext =
    briefingState.type === 'appointments-at-risk'
      ? `Use the following appointment IDs: ${flaggedAppointmentIds}.`
      : undefined;

  function openBriefingOutreachDialog() {
    trackPendoEvent('coordinate_confirmation_dialog_opened', {
      bucket: 'unconfirmed',
      appointment_count: briefingSendableRows.length,
      action_scope: 'card',
      source: 'briefing',
    });
    setBriefingOutreachDialogOpen(true);
  }

  // Only briefing-initiated outreach replaces the briefing with its run summary, which is
  // why the two entry points hold their own dialogs rather than sharing one.
  function recordBriefingOutreach(response: CreateOutreachRunResponse) {
    setBriefingOutreachResult({
      filtersKey,
      runId: response.runId,
    });
  }

  // `source` distinguishes the two entry points for analytics only — never for wording. They differ
  // in what the drawer opens with, which the drawer's own props decide.
  function openBriefingAssistant(source: 'briefing_why_patients' | 'briefing_ask_coordinate') {
    trackPendoEvent('coordinate_data_agent_opened', {
      source,
      agent_status: atRiskPatientsAgentStatusRef.current,
    });
    // Keep the automatic session mounted even if its first response is still pending.
    atRiskPatientsAgentStatusRef.current = 'auto-viewed';
    setAtRiskPatientsDrawerOpen(true);
  }

  function explainFlaggedAppointments() {
    openBriefingAssistant('briefing_why_patients');
  }

  function askCoordinateAnything() {
    openBriefingAssistant('briefing_ask_coordinate');
  }

  function openRiskModelDialog() {
    trackPendoEvent('coordinate_risk_model_opened', { risk_kind: riskKind });
    setRiskModelDialogOpen(true);
  }

  function saveThreshold(nextThreshold: number) {
    setReadinessConfigMutation.mutate({
      orgId: props.me.org.id,
      riskThreshold: nextThreshold,
    });
  }

  function changeAutomaticOutreach(workflow: AutomaticOutreachWorkflow) {
    const control = automaticOutreachControls[workflow];

    if (control.query.data?.enabled) {
      control.mutation.mutate(false);
      return;
    }

    trackPendoEvent('coordinate_automatic_outreach_dialog_opened', {
      workflow,
    });
    setEnableDialogWorkflow(workflow);
  }

  return (
    <>
      <BriefingCard.Root>
        <BriefingCard.Main>
          {briefingState.type === 'outreach-started' ? (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                alignSelf="stretch"
              >
                <BriefingCard.Eyebrow
                  icon={briefingOutreach.icon}
                  color={briefingOutreach.iconColor}
                >
                  {briefingOutreach.eyebrow}
                </BriefingCard.Eyebrow>

                <ActiveFiltersBadge filters={filters} />
              </Stack>

              <BriefingCard.Title>{briefingOutreach.title}</BriefingCard.Title>

              <BriefingCard.Copy>
                <Typography variant="body1">{briefingOutreach.body}</Typography>
              </BriefingCard.Copy>
            </>
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                alignSelf="stretch"
              >
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <BriefingCard.Eyebrow
                    icon={faMessageBot}
                    color={isBriefingRanked ? 'quaternary.light' : 'success.main'}
                  >
                    Daily Briefing
                  </BriefingCard.Eyebrow>

                  <Chip
                    variant="outlined"
                    size="small"
                    icon={<FontAwesomeScalableIcon icon={faSliders} fontSize="inherit" />}
                    label="How this works"
                    onClick={openRiskModelDialog}
                  />
                </Stack>

                <ActiveFiltersBadge filters={filters} />
              </Stack>

              <BriefingCard.Title>
                {greeting}, {props.me.user.displayFirstName}
              </BriefingCard.Title>

              {isBriefingRanked ? (
                <>
                  <BriefingCard.Copy>
                    {/* Counts every Flagged Appointment, the ones already awaiting a reply included:
                        the headline names today's exposure, not the size of the send queue. */}
                    <Typography variant="body1">
                      I found {flaggedCount} {flaggedCount === 1 ? 'appointment' : 'appointments'}{' '}
                      with the highest {attendanceRiskCopy.briefingHeadlinePhrase} in the next few
                      days.
                    </Typography>
                    <Typography variant="body1">
                      {briefingState.type === 'appointments-at-risk'
                        ? "I've already prepared the outreach I believe will have the biggest impact."
                        : getFlaggedAllContactedCopy(flaggedCount)}
                    </Typography>
                  </BriefingCard.Copy>

                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    {briefingState.type === 'appointments-at-risk' ? (
                      <>
                        <ReviewOutreachButton onClick={openBriefingOutreachDialog} />

                        <ExplainFlaggedAppointmentsButton
                          flaggedCount={flaggedCount}
                          onClick={explainFlaggedAppointments}
                        />
                      </>
                    ) : (
                      <AskCoordinateButton onClick={askCoordinateAnything} />
                    )}
                  </Stack>
                </>
              ) : briefingState.type === 'unrankable' ? (
                <>
                  <BriefingCard.Copy>
                    <Typography variant="body1">
                      You have {unconfirmedCount}{' '}
                      {unconfirmedCount === 1 ? 'appointment' : 'appointments'} still waiting on
                      confirmation.
                    </Typography>

                    <Typography variant="body1">{getUnrankableCopy(briefingState)}</Typography>
                  </BriefingCard.Copy>

                  {/* Nothing is ranked here, so there is no ranking to explain — but a screen with
                      nothing left to send is still a place to ask from. */}
                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    {briefingState.hasOutreachToSend ? (
                      <ReviewOutreachButton onClick={openBriefingOutreachDialog} />
                    ) : (
                      <AskCoordinateButton onClick={askCoordinateAnything} />
                    )}
                  </Stack>
                </>
              ) : (
                <BriefingCard.Copy>
                  <Typography variant="body1">
                    I&apos;ll update you here when there&apos;s critical action to be taken.
                  </Typography>

                  <Typography variant="body1">
                    Things are looking good right now. Check back later.
                  </Typography>
                </BriefingCard.Copy>
              )}
            </>
          )}
        </BriefingCard.Main>

        {isBriefingRanked ? (
          <BriefingCard.Aside>
            <Typography variant="body2" color="text.secondary">
              <FontAwesomeScalableIcon
                icon={faChartLineUp}
                fontSize="small"
                sx={{ color: 'success.main', mr: 1, verticalAlign: 'text-bottom' }}
              />
              At your practice, patients who confirm within 24 hours are{' '}
              <Box component="span" fontWeight={600}>
                2.1x less likely
              </Box>{' '}
              to late-cancel.
            </Typography>
          </BriefingCard.Aside>
        ) : null}
      </BriefingCard.Root>

      {/* Card's overflow clipping prevents browsers from honoring its own scroll margin. */}
      <Box
        ref={readinessContainerRef}
        data-testid="readiness-container"
        sx={{ scrollMarginTop: (theme) => theme.spacing(readinessScrollGapSpacing) }}
      >
        <Card sx={{ padding: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            <div>
              <Typography component="h1" variant="h6">
                Patient Readiness
              </Typography>
              <Typography component="p" variant="body2" color="text.secondary">
                Flagged patients with upcoming appointments
              </Typography>
            </div>
          </Stack>

          <RadioGroup
            aria-label="Readiness buckets"
            name="readiness-bucket"
            value={selectedBucket}
            onChange={(event) => selectBucket(getSelectedBucket(event.target.value))}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <ReadinessBucketCard.Root
              color={unconfirmedBucket.color}
              value={unconfirmedBucket.key}
              onSelect={selectUnconfirmedCard}
              data-testid={`bucket-${unconfirmedBucket.key}`}
            >
              <ReadinessBucketCard.Count value={unconfirmedCount} />

              <ReadinessBucketCard.Adornment>
                <ActiveFiltersBadge filters={filters} />
              </ReadinessBucketCard.Adornment>

              <ReadinessBucketCard.Content
                label={attendanceRiskCopy.bucketMetricLabel}
                emphasis={unconfirmedBucket.emphasis}
              />

              <ReadinessBucketCard.Divider />

              <ReadinessBucketCard.Action>
                <ReadinessCtaButton
                  me={props.me}
                  filters={filters}
                  color={unconfirmedBucket.color}
                  label="Review Outreach"
                  rows={cardSendableRows}
                  actionScope="card"
                  isCardSelected={selectedBucket === unconfirmedBucket.key}
                  onSelectCard={selectUnconfirmedCard}
                  disabled={cardSendableRows.length === 0}
                />
              </ReadinessBucketCard.Action>

              <ReadinessBucketCard.AutoRun
                checked={confirmationOutreachEnabled}
                disabled={confirmationOutreachControlDisabled}
                accessibleLabel={
                  confirmationOutreachConfigUnavailable
                    ? 'Automatic confirmation outreach unavailable'
                    : 'Automatic confirmation outreach'
                }
                label={confirmationOutreachConfigUnavailable ? 'Unavailable' : 'Auto'}
                onChange={() => changeAutomaticOutreach('confirmation')}
              />
            </ReadinessBucketCard.Root>

            <ReadinessBucketCard.Root
              color={rescheduleBucket.color}
              value={rescheduleBucket.key}
              onSelect={selectRescheduleCard}
              data-testid={`bucket-${rescheduleBucket.key}`}
            >
              <ReadinessBucketCard.Count value={query.data.reschedulePatientCount} />
              <ReadinessBucketCard.Adornment>
                <ActiveFiltersBadge filters={filters} />
              </ReadinessBucketCard.Adornment>
              <ReadinessBucketCard.Content
                label={rescheduleBucket.metricLabel}
                emphasis={rescheduleBucket.emphasis}
                detail={
                  rebookingAutomaticOutreach.query.data
                    ? `Interaction Mode: ${interactionModeLabels[rebookingAutomaticOutreach.query.data.effectiveInteractionMode]}`
                    : undefined
                }
              />
              <ReadinessBucketCard.Divider />

              <ReadinessBucketCard.Action>
                <RebookingCtaButton
                  me={props.me}
                  filters={filters}
                  messageAuthorName={query.data.messageAuthorName}
                  label="Review Outreach"
                  actionScope="card"
                  outreach={rescheduleOutreach}
                  isCardSelected={selectedBucket === rescheduleBucket.key}
                  onSelectCard={selectRescheduleCard}
                  disabled={rescheduleOutreach.rows.length === 0}
                />
              </ReadinessBucketCard.Action>
              <ReadinessBucketCard.AutoRun
                checked={rebookingOutreachEnabled}
                disabled={rebookingOutreachControlDisabled}
                accessibleLabel={
                  rebookingOutreachConfigUnavailable
                    ? 'Automatic rebooking outreach unavailable'
                    : 'Automatic rebooking outreach'
                }
                label={rebookingOutreachConfigUnavailable ? 'Unavailable' : 'Auto'}
                onChange={() => changeAutomaticOutreach('rebooking')}
              />
            </ReadinessBucketCard.Root>

            <ReadinessBucketCard.Root
              color={waitlistBucket.color}
              value={waitlistBucket.key}
              onSelect={selectWaitlistCard}
              data-testid={`bucket-${waitlistBucket.key}`}
            >
              <ReadinessBucketCard.Count
                value={slotFillEnabled ? offerableSlotRows.length : '--'}
              />
              {/* Identical disclosure copy despite the waitlist-side filter semantics — by design. */}
              <ReadinessBucketCard.Adornment>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    component="button"
                    type="button"
                    variant="outlined"
                    size="small"
                    color={waitlistBucket.color}
                    label={
                      activeWaitlistQuery.data?.totalCount == null
                        ? '-- candidates'
                        : pluralize(activeWaitlistQuery.data.totalCount, 'candidate')
                    }
                    aria-label="View waitlist"
                    icon={<FontAwesomeScalableIcon icon={faUserGroupSolid} fontSize="inherit" />}
                    // Non-squared FA icon makes it look weird without this one-off padding
                    sx={{ pl: 0.5 }}
                    disabled={!slotFillEnabled}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (selectedBucket !== waitlistBucket.key) {
                        selectWaitlistCard();
                        return;
                      }
                      setWaitlistDialogOpen(true);
                    }}
                  />
                  <ActiveFiltersBadge filters={filters} />
                </Stack>
              </ReadinessBucketCard.Adornment>
              <ReadinessBucketCard.Content
                label={waitlistBucket.metricLabel}
                emphasis={waitlistBucket.emphasis}
              />
              <ReadinessBucketCard.Divider />

              <ReadinessBucketCard.Action>
                <OfferSlotsAction
                  me={props.me}
                  filters={filters}
                  offerBatchSize={slotFillData?.offerBatchSize ?? 0}
                  label="Review Outreach"
                  actionScope="card"
                  slots={actionableOfferableSlotRows}
                  totalSlotCount={offerableSlotRows.length}
                  isCardSelected={selectedBucket === waitlistBucket.key}
                  onSelectCard={selectWaitlistCard}
                  disabled={!slotFillEnabled || actionableOfferableSlotRows.length === 0}
                />
              </ReadinessBucketCard.Action>
            </ReadinessBucketCard.Root>
          </RadioGroup>

          <Box mt={2}>
            {selectedBucket === 'unconfirmed' ? (
              <UnconfirmedDetailsPanel me={props.me} filters={filters} rows={unconfirmedRows} />
            ) : selectedBucket === 'reschedule' ? (
              <RescheduleDetailsPanel
                me={props.me}
                filters={filters}
                messageAuthorName={query.data.messageAuthorName}
                rows={rescheduleRows}
              />
            ) : slotFillEnabled && slotFillData ? (
              <OfferableSlotsDetailsPanel
                me={props.me}
                filters={filters}
                offerBatchSize={slotFillData.offerBatchSize}
                slots={offerableSlotRows}
              />
            ) : (
              <SlotFillUnavailableDetails />
            )}
          </Box>
        </Card>
      </Box>

      {enableDialogWorkflow && automaticOutreachControls[enableDialogWorkflow].query.data ? (
        <AutomaticOutreachEnableDialog
          workflow={enableDialogWorkflow}
          localHour={automaticOutreachControls[enableDialogWorkflow].query.data.localHour}
          isSubmitting={automaticOutreachControls[enableDialogWorkflow].mutation.isPending}
          onClose={() => setEnableDialogWorkflow(null)}
          onConfirm={() => {
            automaticOutreachControls[enableDialogWorkflow].mutation.mutate(true, {
              onSuccess: () => setEnableDialogWorkflow(null),
            });
          }}
        />
      ) : null}

      {isWaitlistDialogOpen && slotFillEnabled ? (
        <WaitlistDialog orgId={orgId} onClose={() => setWaitlistDialogOpen(false)} />
      ) : null}

      {hasBriefingAssistant ? (
        <DataAgent.Drawer open={isAtRiskPatientsDrawerOpen} onClose={closeAtRiskPatientsDrawer}>
          <DataAgent.Iframe
            initialPrivateContext={dataAgentInitialPrivateContext}
            initialPrompt={dataAgentInitialPrompt}
            isInitialAutoScrollEnabled={false}
            onResponse={handleAtRiskPatientsAutoDetectResponse}
          />
        </DataAgent.Drawer>
      ) : null}

      {isBriefingOutreachDialogOpen ? (
        <ConfirmationOutreachDialog
          me={props.me}
          filters={filters}
          rows={briefingSendableRows}
          actionScope="card"
          onClose={() => setBriefingOutreachDialogOpen(false)}
          onStarted={recordBriefingOutreach}
        />
      ) : null}

      {isRiskModelDialogOpen ? (
        <RiskModelDialog
          onClose={() => setRiskModelDialogOpen(false)}
          orgName={props.me.org.name}
          explainerState={toExplainerState(riskModelExplainerQuery)}
          thresholdState={thresholdState}
          riskProbabilities={riskProbabilities}
          canEdit={canEditThreshold}
          isSaving={setReadinessConfigMutation.isPending}
          onSaveThreshold={saveThreshold}
        />
      ) : null}
    </>
  );
}

const readinessTimeFormat = 'h:mmaaa';

// One spelling of "nothing to show here" for every readiness cell, so the grids can't drift onto
// different placeholders.
const emptyCellPlaceholder = '--';

const briefingOutreachStatusDescription = 'See Status updates for each patient in the table below';

/** How the briefing banner reports the run behind it, across the run's three staff-visible states. */
interface BriefingOutreachPresentation {
  body: string;
  eyebrow: string;
  icon: IconDefinition;
  iconColor: string;
  title: string;
}

/**
 * The banner's three states, in one place rather than as the same condition re-tested per slot.
 *
 * `run` is undefined until the first read of the run resource lands, and stays undefined if that
 * read fails — both render as still-running. A run whose end we cannot see is not one to call
 * finished, so the banner overstates activity rather than claiming a completion that may not have
 * happened. `failed` is admission failure, where no attempt was ever created: reporting it as
 * complete would read "0 messages sent" over a run that never started.
 */
function getBriefingOutreachPresentation(
  run: OutreachRunSummary | undefined
): BriefingOutreachPresentation {
  if (run?.status === 'failed') {
    return {
      body: 'The outreach could not be started, so no messages went out. Try again, and let us know if it keeps failing.',
      eyebrow: 'Outreach Failed',
      icon: faTriangleExclamation,
      iconColor: 'error.main',
      title: `I couldn't start outreach for ${pluralize(run.targetCount, 'appointment')}`,
    };
  }

  if (run && isTerminalRunStatus(run.status)) {
    return {
      body: briefingOutreachStatusDescription,
      eyebrow: 'Outreach Complete',
      icon: faCircleCheckSolid,
      iconColor: 'success.main',
      title: "I've completed outreach to the high risk appointments in the next 2 days",
    };
  }

  return {
    body: briefingOutreachStatusDescription,
    eyebrow: 'Outreach Started',
    icon: faCircleCheckSolid,
    iconColor: 'success.main',
    title: "I've begun outreach to the high risk appointments in the next 2 days",
  };
}

// Same-day ranges drop the redundant second date; a range crossing midnight keeps both.
function formatReadinessTimeRange(start: Date, end: Date): string {
  const endFormat = isSameDay(start, end) ? readinessTimeFormat : readinessDateTimeFormat;
  return `${formatReadinessDateTime(start)} - ${format(end, endFormat)}`;
}

function getPatientAge(patientBirthDate: string | null): number | null {
  if (!patientBirthDate) {
    return null;
  }

  return differenceInYears(new Date(), parseISO(patientBirthDate));
}

function formatPatientPhone(patientPhone: string | null): string {
  return patientPhone ? formatPhoneNumber(patientPhone) : emptyCellPlaceholder;
}

function formatPatientAge(patientAge: number | null): string {
  return patientAge === null ? emptyCellPlaceholder : String(patientAge);
}

function formatAttendanceHistory(riskFactors: PatientRiskFactors): string {
  return `${riskFactors.noShowCount} / ${riskFactors.lateCancelCount}`;
}

function formatRiskProbability(probability: number | null): string {
  return probability === null ? emptyCellPlaceholder : `${Math.round(probability * 100)}%`;
}

function formatPersonName(person: { firstName?: string | null; lastName?: string | null }) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ');
  return name || emptyCellPlaceholder;
}

function formatUnconfirmedPatientName(row: UnconfirmedRow) {
  return formatPersonName({ firstName: row.patientFirstName, lastName: row.patientLastName });
}

function formatSlotProviderName(
  slot: Pick<OfferableSlot, 'providerFirstName' | 'providerLastName'>
) {
  return formatPersonName({ firstName: slot.providerFirstName, lastName: slot.providerLastName });
}

type PortalEntity = 'patient' | 'provider';

function PortalEntityLink(props: { entity: PortalEntity; id: number; children: React.ReactNode }) {
  return (
    <Link
      href={new URL(`/${props.entity}/${props.id}`, config.PORTAL_BASE_URL).toString()}
      target="_blank"
      rel="noopener noreferrer"
      color="text.primary"
    >
      {props.children}
    </Link>
  );
}

// Bulk sends generate no preview, but every included recipient is SMS-reachable while email
// execution availability is not exposed to this client.
const bulkChannelLabel = 'SMS';

const channelDisplayLabels = { sms: 'SMS', email: 'email' } as const satisfies Record<
  MiaMessageChannel,
  string
>;

// The bulk template takes after the deterministic fallback in care-coordinator-agent
// (apps/api/src/skills/confirmation-reminder.ts), but it is illustrative only — the actual message
// is always written server-side at send time.
// It's perfectly fine if this wording drifts from the server template; don't feel obligated to
// keep it word-for-word in sync.

// The confirm link is tokenized server-side at send time, so template copy renders it as a
// bracketed placeholder — same convention as the template's per-recipient fields.
const confirmationLinkPlaceholder = '[Confirmation Link]';

function getBulkMessageIntroduction(senderFirstName: User['firstName'], orgName: string): string {
  return `Hi [Patient Name], this is ${senderFirstName} with ${orgName}.`;
}

function getBulkConfirmationMessageTemplate(
  orgName: string,
  senderFirstName: User['firstName']
): string {
  return `${getBulkMessageIntroduction(senderFirstName, orgName)} I noticed you haven't confirmed your appointment with [Provider] on [Appointment Date]. Will you be there? Please click this link to confirm: ${confirmationLinkPlaceholder}`;
}

function getSendMessagesConfirmLabel(recipientCount: number): string {
  return recipientCount > 1 ? `Send ${recipientCount} messages` : 'Send message';
}

function getConfirmationDialogTitle(recipientCount: number): string {
  return recipientCount > 1
    ? `Send all ${recipientCount} confirmation requests`
    : 'Send confirmation request';
}

// The vetted copy the single-send dialog previewed, carried from the dialog to the send so the
// dispatched message is verbatim what staff saw.
type PreparedMessage = Pick<PreviewMessageContentResponse, 'body' | 'subject' | 'channel'>;

function notifySinglePreparedOutcome(response: SendPreparedResponse) {
  if (response.outcome === 'sent') {
    enqueueSnackbar('Sent 1 confirmation request.', { variant: 'success' });
    return;
  }

  enqueueSnackbar('1 confirmation request could not be sent.', { variant: 'warning' });
}

function notifySendRemindersStarted(response: CreateOutreachRunResponse) {
  enqueueSnackbar(
    `Confirmation outreach started for ${pluralize(response.targetCount, 'appointment')}.`,
    { variant: 'success' }
  );
}

/**
 * The confirmation-request dialog and the send it dispatches, mounted only while open.
 *
 * Owned by a component of its own rather than by the button that opens it, because the two entry
 * points into it act on different populations — the briefing sends to what it ranked, the
 * Unconfirmed Appointments card to everything it lists — so they can't share one instance. That
 * split is also what confines the briefing's "Outreach Sent" summary to briefing-initiated sends:
 * only the briefing passes `onStarted`.
 */
function ConfirmationOutreachDialog(props: {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  rows: UnconfirmedRow[];
  actionScope: 'card' | 'row';
  onClose: () => void;
  onStarted?: (response: CreateOutreachRunResponse) => void;
}) {
  const queryClient = useQueryClient();
  const readinessQuery = activeReadinessQuery(props.me.org.id, props.filters);
  const sendableRows = props.rows.filter(isSmsReachableUnconfirmedRow);
  const isBulk = sendableRows.length > 1;
  const soleRow = sendableRows[0];

  // Bulk-only: reached solely by the bulk dialog, which mounts only for more than one row. Single-row
  // sends go through `sendPreparedMutation` instead.
  const sendRemindersMutation = useMutation({
    mutationFn: (request: SendRemindersRequest) => sendReminders(request),
    onSuccess: (response) => {
      notifySendRemindersStarted(response);

      // A bulk send moves many rows at once — let the server recompute the worklist rather than
      // hand-patching every row's outreach state.
      void queryClient.invalidateQueries({ queryKey: readinessQuery.queryKey });

      props.onStarted?.(response);
      props.onClose();
    },
    onError: () => {
      enqueueSnackbar('Could not send these confirmation requests. Please try again later.', {
        variant: 'error',
      });
    },
  });

  const sendPreparedMutation = useMutation({
    mutationFn: (request: SendPreparedRequest) => sendPrepared(request),
    onSuccess: (response) => {
      notifySinglePreparedOutcome(response);

      // A skipped/failed outcome is terminal for this send (invalid address, missing recipient,
      // dispatch failure) — leave the dialog open with the warning so Cancel is the exit.
      if (response.outcome !== 'sent' || !soleRow) {
        return;
      }

      const sentAppointmentId = soleRow.appointmentId;

      queryClient.setQueryData<PatientReadinessDetailsResponse>(
        readinessQuery.queryKey,
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          // A verbatim single send only touches one appointment — patch just that row instead of
          // refetching the whole worklist. The send's `conversationId` is the one the worklist row
          // would carry, so the patched row satisfies the awaiting_confirmation variant;
          // `lastContactedAt` is the client clock, close enough until the next poll replaces it
          // with the server's timestamp.
          return {
            ...currentData,
            unconfirmed: currentData.unconfirmed.map((row) => {
              if (row.appointmentId !== sentAppointmentId || row.status === 'confirmed') {
                return row;
              }

              return {
                ...row,
                status: 'awaiting_confirmation' as const,
                lastContactedAt: new Date().toISOString(),
                contactCount: row.contactCount + 1,
                conversationId: response.conversationId,
              };
            }),
          };
        }
      );

      props.onClose();
    },
    onError: () => {
      enqueueSnackbar('Could not send this confirmation request. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function confirmAndSend() {
    const appointmentIds = sendableRows.map((row) => row.appointmentId);
    trackPendoEvent('coordinate_confirmation_requests_sent', {
      bucket: 'unconfirmed',
      appointment_count: appointmentIds.length,
      action_scope: props.actionScope,
    });
    sendRemindersMutation.mutate({ orgId: props.me.org.id, appointmentIds });
  }

  // The single-send branch dispatches the exact copy the dialog previewed via `sendPrepared`,
  // rather than routing through the bulk `sendReminders` path that regenerates copy server-side.
  function confirmSinglePrepared(prepared: PreparedMessage) {
    if (!soleRow) {
      return;
    }

    trackPendoEvent('coordinate_confirmation_requests_sent', {
      bucket: 'unconfirmed',
      appointment_count: 1,
      action_scope: props.actionScope,
    });
    sendPreparedMutation.mutate({
      link: { topic: 'appointment-confirmation', appointmentId: soleRow.appointmentId },
      channel: prepared.channel,
      body: prepared.body,
      subject: prepared.subject,
    });
  }

  // The recipient set stays live rather than being snapshotted on open — the worklist and the
  // threshold both poll — so it can empty under an open dialog. Render nothing there; the server's
  // own send-time state checks are what absorb the narrower drift of a patient confirming mid-review.
  if (!soleRow) {
    return null;
  }

  return isBulk ? (
    <RequestConfirmationDialog
      isBulk
      onClose={props.onClose}
      onConfirm={confirmAndSend}
      status={sendRemindersMutation.isPending ? 'submitting' : 'idle'}
      title={getConfirmationDialogTitle(sendableRows.length)}
      message={getBulkConfirmationMessageTemplate(props.me.org.name, props.me.user.firstName)}
      channelLabel={bulkChannelLabel}
      confirmLabel={getSendMessagesConfirmLabel(sendableRows.length)}
      recipientCount={sendableRows.length}
    >
      <ReadinessDataGrid
        rows={sendableRows}
        columns={unconfirmedRecipientColumns}
        getRowId={getAppointmentRowId}
        variant="dialog"
      />
    </RequestConfirmationDialog>
  ) : (
    <SingleRequestConfirmationDialog
      row={soleRow}
      onClose={props.onClose}
      onConfirm={confirmSinglePrepared}
      isSending={sendPreparedMutation.isPending}
    />
  );
}

function ReadinessCtaButton(props: {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  label: string;
  rows: UnconfirmedRow[];
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  size?: ButtonProps['size'];
  actionScope: 'card' | 'row';
  // The row scope only ever renders once its owning card is already selected (it's mounted from
  // the details panel below the bucket cards), so that call site can pass this as a static true.
  isCardSelected: boolean;
  // Card-scoped only: selects the enclosing card instead of firing the CTA when it's clicked
  // before being selected.
  onSelectCard?: () => void;
  disabled?: boolean;
}) {
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);

  function openConfirmationDialog() {
    // Card-scoped CTAs live inside a selectable card — the first click on an inactive card
    // should only select it, not also fire the CTA.
    if (!props.isCardSelected) {
      props.onSelectCard?.();
      return;
    }

    if (props.rows.length === 0) {
      enqueueSnackbar('There are no appointments to confirm.', { variant: 'error' });
      return;
    }

    trackPendoEvent('coordinate_confirmation_dialog_opened', {
      bucket: 'unconfirmed',
      appointment_count: props.rows.length,
      action_scope: props.actionScope,
    });
    setConfirmationDialogOpen(true);
  }

  return (
    <>
      <Button
        variant={props.actionScope === 'card' ? 'contained' : 'outlined'}
        color={props.color}
        startIcon={<FontAwesomeScalableIcon icon={faBell} />}
        sx={{ flexShrink: 0 }}
        size={props.size ?? 'medium'}
        disabled={props.disabled}
        onClick={openConfirmationDialog}
      >
        {props.label}
      </Button>

      {isConfirmationDialogOpen ? (
        <ConfirmationOutreachDialog
          me={props.me}
          filters={props.filters}
          rows={props.rows}
          actionScope={props.actionScope}
          onClose={() => setConfirmationDialogOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Single-appointment confirmation dialog: previews the actual copy Mia would send (generated
 * server-side) rather than the client-side template the bulk flow shows. Mounted only while
 * open so the preview request fires exactly when the dialog appears.
 */
function SingleRequestConfirmationDialog(props: {
  row: SmsReachableRow<UnconfirmedRow>;
  onClose: () => void;
  onConfirm: (prepared: PreparedMessage) => void;
  isSending: boolean;
}) {
  const requestedChannel: MiaMessageChannel = 'sms';

  const previewQuery = useQuery(
    miaMessagePreview({
      kind: 'confirmation',
      appointmentId: props.row.appointmentId,
      channel: requestedChannel,
    })
  );

  // Confirm sends the previewed copy verbatim. The button is only enabled once the preview
  // succeeds, so `previewQuery.data` is present here; the server-resolved channel is used, not
  // the client's requested channel prediction.
  function confirmPrepared() {
    if (!previewQuery.isSuccess) {
      return;
    }

    props.onConfirm({
      body: previewQuery.data.body,
      subject: previewQuery.data.subject,
      channel: previewQuery.data.channel,
    });
  }

  const patientName = formatUnconfirmedPatientName(props.row);
  const appointmentLabel = formatReadinessDateTime(props.row.appointmentStartsAt);

  // Staff confirm exactly the copy they previewed, so a failed preview blocks the send (the
  // dialog's `error` status disables confirming) instead of falling back to template copy that
  // may not match what Mia would actually write.
  const message = previewQuery.isPending ? (
    <Skeleton data-testid="confirmation-message-skeleton" />
  ) : previewQuery.isError ? (
    <Typography component="span" variant="inherit" color="error.main">
      Message preview could not be loaded. Please try again later.
    </Typography>
  ) : (
    previewQuery.data.body
  );

  // The requested channel is only the client's prediction — the caption shows the channel the
  // preview actually resolved to, with an inline skeleton just for that word while it loads.
  const channelLabel = previewQuery.isPending ? (
    <Skeleton width={40} sx={{ display: 'inline-block' }} />
  ) : (
    channelDisplayLabels[previewQuery.data?.channel ?? requestedChannel]
  );

  return (
    <RequestConfirmationDialog
      onClose={props.onClose}
      onConfirm={confirmPrepared}
      status={
        props.isSending
          ? 'submitting'
          : previewQuery.isPending
            ? 'loading'
            : previewQuery.isError
              ? 'error'
              : 'idle'
      }
      title={getConfirmationDialogTitle(1)}
      message={message}
      channelLabel={channelLabel}
      confirmLabel={getSendMessagesConfirmLabel(1)}
      recipient={{
        id: props.row.appointmentId,
        name: patientName,
        appointmentLabel,
        providerName: props.row.providerName,
      }}
    />
  );
}

const hasJoinedWaitlistSchema = z.boolean();

/**
 * Shared by `ComingSoonBucketCard` (immediate join) and `MiaProWaitlistDialog` (join from within
 * the dialog) so the two entry points read/write the same flag instead of duplicating the
 * `useLocalState` wiring.
 */
function useMiaProWaitlist(me: GetMeResponsePayload) {
  const [hasJoinedWaitlist, setHasJoinedWaitlist] = useLocalState({
    key: `mia-readiness-actions-waitlist-${me.user.id}-${me.org.id}`,
    defaultValue: false,
    schema: hasJoinedWaitlistSchema,
  });

  function joinWaitlist() {
    setHasJoinedWaitlist(true);
    trackPendoEvent('coordinate_waitlist_registered');
  }

  return { hasJoinedWaitlist, joinWaitlist };
}

function ComingSoongChip(props: Pick<ChipProps, 'color'>) {
  return (
    <Chip
      label="COMING SOON"
      color={props.color}
      size="small"
      variant="outlined"
      icon={<FontAwesomeScalableIcon icon={faSparkles} />}
    />
  );
}

/**
 * A readiness bucket rendered as a "coming soon" teaser with an early-access join CTA. Not
 * currently mounted — retained as the reusable treatment for the next bucket that ships behind
 * early access; wire it in place of a `ReadinessBucketCard.Root` when that card lands.
 *
 * @public — intentionally exported-but-unused for now; keeps knip from flagging the retained
 * treatment as dead code.
 */
export function ComingSoonBucketCard(props: {
  me: GetMeResponsePayload;
  bucket: typeof rescheduleBucket | typeof waitlistBucket;
  count: React.ReactNode;
}) {
  const { hasJoinedWaitlist, joinWaitlist } = useMiaProWaitlist(props.me);

  return (
    <ReadinessBucketCard.Root
      color={props.bucket.color}
      value={props.bucket.key}
      data-testid={`bucket-${props.bucket.key}`}
    >
      <ReadinessBucketCard.Count value={props.count} />

      <ReadinessBucketCard.Adornment>
        <ComingSoongChip color={props.bucket.color} />
      </ReadinessBucketCard.Adornment>

      <ReadinessBucketCard.Content
        label={props.bucket.metricLabel}
        emphasis={props.bucket.emphasis}
      />

      <ReadinessBucketCard.Divider />

      <ReadinessBucketCard.Action>
        {hasJoinedWaitlist ? (
          <Typography variant="caption" color="success.main">
            You&apos;re on the early access list!
          </Typography>
        ) : (
          <Button
            color={props.bucket.color}
            variant="contained"
            startIcon={<FontAwesomeScalableIcon icon={faBolt} />}
            onClick={joinWaitlist}
          >
            Express Interest in Early Access
          </Button>
        )}
      </ReadinessBucketCard.Action>
    </ReadinessBucketCard.Root>
  );
}

const dataGridAutosizeOptions = { expand: true } as const;
const detailsEstimatedRowHeight = 72;
const dialogDataGridMaxHeight = 400;
const dialogDataGridContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: dialogDataGridMaxHeight,
} as const;

type ReadinessDataGridColor = 'error' | 'warning' | 'quaternary';
type ReadinessCsvFileName = 'unconfirmed' | 'reschedule' | 'offerable-slots';
type DataGridInitialState = NonNullable<DataGridProProps<GridValidRowModel>['initialState']>;
type DataGridSlotProps = NonNullable<DataGridProProps<GridValidRowModel>['slotProps']>;

// Every details grid appends its row CTA as an `action` column and pins it to the right, so the
// action stays visible however far the other columns overflow horizontally.
const detailsActionPinnedColumns = {
  right: ['action'],
} satisfies DataGridInitialState['pinnedColumns'];

const unconfirmedDataGridInitialState = {
  pinnedColumns: detailsActionPinnedColumns,
  sorting: {
    sortModel: [
      { field: 'riskProbability', sort: 'desc' },
      { field: 'appointmentDate', sort: 'asc' },
    ],
  },
} satisfies DataGridInitialState;
const rescheduleDataGridInitialState = {
  pinnedColumns: detailsActionPinnedColumns,
  sorting: { sortModel: [{ field: 'appointmentDate', sort: 'asc' }] },
} satisfies DataGridInitialState;
const offerableSlotsDataGridInitialState = {
  pinnedColumns: detailsActionPinnedColumns,
  sorting: { sortModel: [{ field: 'appointmentTime', sort: 'asc' }] },
} satisfies DataGridInitialState;

// Every readiness toolbar offers CSV and hides print; only the download's filename differs. Built
// once per grid at module scope so the identity stays stable across renders.
function createToolbarSlotProps(csvFileName: ReadinessCsvFileName): DataGridSlotProps {
  return {
    toolbar: {
      csvOptions: { fileName: csvFileName },
      printOptions: { disableToolbarButton: true },
    },
  };
}

const dataGridToolbarSlotProps = {
  unconfirmed: createToolbarSlotProps('unconfirmed'),
  reschedule: createToolbarSlotProps('reschedule'),
  'offerable-slots': createToolbarSlotProps('offerable-slots'),
} satisfies Record<ReadinessCsvFileName, DataGridSlotProps>;

function createReadinessDataGridSx(
  borderTopColor: string
): NonNullable<DataGridProProps<GridValidRowModel>['sx']> {
  return (theme) => ({
    // Border colors
    '--DataGrid-rowBorderColor': theme.palette.divider,
    borderColor: 'divider',
    borderTopColor,
    [`& .${gridClasses.withBorderColor}`]: {
      borderColor: 'divider',
    },
    [`& .${gridClasses.toolbar}`]: {
      borderBottomColor: 'divider',
    },
    [`& .${gridClasses.columnHeader} .${gridClasses.columnSeparator}`]: {
      color: 'divider',
    },
    [`& .${gridClasses.cell}`]: { py: 1, alignItems: 'center', display: 'flex' },
    [`&.${gridClasses['root--densityCompact']} .${gridClasses.cell}`]: { py: 0.75 },
  });
}

// A details grid wears its bucket's color on the top border; a dialog grid sits inside an already
// color-themed dialog and keeps that border neutral — so the dialog variant has no color axis at
// all, and its call sites don't pass one.
const readinessDataGridSx = {
  details: {
    error: createReadinessDataGridSx('error.main'),
    warning: createReadinessDataGridSx('warning.main'),
    quaternary: createReadinessDataGridSx('quaternary.main'),
  },
  dialog: createReadinessDataGridSx('divider'),
} as const;

function getDetailsRowHeight() {
  return 'auto' as const;
}

function getDetailsEstimatedRowHeight() {
  return detailsEstimatedRowHeight;
}

function getAppointmentRowId(row: { appointmentId: number }): number {
  return row.appointmentId;
}

interface ReadinessDataGridBaseProps<TRow extends GridValidRowModel> {
  rows: TRow[];
  columns: GridColDef<TRow>[];
  getRowId?: (row: TRow) => number;
}

interface ReadinessDetailsDataGridProps {
  color: ReadinessDataGridColor;
  csvFileName: ReadinessCsvFileName;
  initialState: DataGridInitialState;
  variant: 'details';
}

interface ReadinessDialogDataGridProps {
  color?: never;
  csvFileName?: never;
  initialState?: never;
  variant: 'dialog';
}

type ReadinessDataGridProps<TRow extends GridValidRowModel> = ReadinessDataGridBaseProps<TRow> &
  (ReadinessDetailsDataGridProps | ReadinessDialogDataGridProps);

function ReadinessDataGrid<TRow extends GridValidRowModel>(props: ReadinessDataGridProps<TRow>) {
  const apiRef = useGridApiRef();
  const isDialog = props.variant === 'dialog';

  // `autosizeOnMount` sizes columns exactly once — when a filter change rebuilds the column defs
  // (their renderCell closures carry the active filters), the grid would otherwise reset every
  // column to its default width and leave the row half-empty. Re-run the same autosize whenever
  // the columns actually change; the mount itself stays covered by `autosizeOnMount`.
  const mountedColumnsRef = React.useRef(props.columns);
  React.useEffect(() => {
    if (mountedColumnsRef.current === props.columns) {
      return;
    }

    mountedColumnsRef.current = props.columns;
    void apiRef.current?.autosizeColumns(dataGridAutosizeOptions);
  }, [apiRef, props.columns]);

  const dataGrid = (
    <DataGridPro
      apiRef={apiRef}
      rows={props.rows}
      columns={props.columns}
      getRowId={props.getRowId}
      density={isDialog ? 'compact' : 'standard'}
      hideFooter
      showToolbar={!isDialog}
      disableRowSelectionOnClick
      disableColumnSelector
      disableColumnFilter
      disableColumnMenu={isDialog}
      disableColumnReorder={isDialog}
      disableColumnResize={isDialog}
      disableColumnSorting={isDialog}
      autosizeOnMount
      autosizeOptions={dataGridAutosizeOptions}
      getRowHeight={getDetailsRowHeight}
      getEstimatedRowHeight={getDetailsEstimatedRowHeight}
      sx={
        props.variant === 'dialog'
          ? readinessDataGridSx.dialog
          : readinessDataGridSx.details[props.color]
      }
      initialState={props.initialState}
      slotProps={props.csvFileName ? dataGridToolbarSlotProps[props.csvFileName] : undefined}
    />
  );

  if (!isDialog) {
    return dataGrid;
  }

  return <Box sx={dialogDataGridContainerSx}>{dataGrid}</Box>;
}

const statusCellTimeFormat = 'hh:mm aa';
const needsOutreachStatusLabel = 'Needs outreach';
const patientUnreachableStatusLabel = 'Patient Unreachable';

function getOutreachStatusLabel(outreach: OutreachProjection): string {
  const triggerLabel = outreach.trigger === 'scheduled' ? 'Automatic' : 'Manual';
  let statusLabel: string;

  if (outreach.unrecordedContactCount > 0) {
    statusLabel = `Sent · ${pluralize(outreach.unrecordedContactCount, 'unrecorded contact')}`;
    return `${statusLabel} · ${triggerLabel}`;
  }

  switch (outreach.status) {
    case 'queued':
      statusLabel = 'Outreach queued';
      break;
    case 'processing':
      statusLabel = 'Sending outreach';
      break;
    case 'retrying':
      statusLabel = 'Retrying outreach';
      break;
    case 'accepted':
      statusLabel = `Outreach Sent - ${format(new Date(outreach.updatedAt), statusCellTimeFormat)}`;
      break;
    case 'failed':
      statusLabel = 'Outreach failed';
      break;
    case 'skipped':
      statusLabel = 'Outreach skipped';
      break;
    case 'delivery_unknown':
      statusLabel = 'Delivery unknown';
      break;
  }

  return `${statusLabel} · ${triggerLabel}`;
}

// "Delivered" (not "awaiting confirmation") per design: the cell reports what happened to the
// outreach, while the pending outcome is implied by the row still counting as at-risk.
function getConfirmationStatusLabel(row: UnconfirmedRow): string {
  switch (row.status) {
    case 'confirmed':
      return `Confirmed · ${format(new Date(row.confirmedAt), statusCellTimeFormat)}`;
    case 'awaiting_confirmation':
      return row.outreach
        ? getOutreachStatusLabel(row.outreach)
        : `Outreach sent - ${format(new Date(row.lastContactedAt), statusCellTimeFormat)}`;
    case 'not_contacted':
      if (row.outreach) {
        return getOutreachStatusLabel(row.outreach);
      }

      return row.preferredChannel === 'sms'
        ? needsOutreachStatusLabel
        : patientUnreachableStatusLabel;
  }
}

/**
 * What both worklists carry about the patient behind a row. The unconfirmed and rebooking grids
 * present these three identically, so they share one set of column definitions rather than two
 * hand-synced copies.
 */
interface WorklistPatientRow {
  patientBirthDate: string | null;
  patientPhone: string | null;
  riskFactors: PatientRiskFactors;
}

function getPatientContactColumns<
  TRow extends GridValidRowModel & WorklistPatientRow,
>(): GridColDef<TRow>[] {
  // Both columns keep the raw value in `valueGetter` so they sort on the underlying age and number,
  // and put the empty-cell placeholder in `valueFormatter` — one convention for where a placeholder
  // lives, rather than one column deciding it per hook.
  return [
    {
      field: 'patientAge',
      headerName: 'Age',
      valueGetter: (_value, row) => getPatientAge(row.patientBirthDate),
      valueFormatter: (value: number | null) => formatPatientAge(value),
    },
    {
      field: 'patientPhone',
      headerName: 'Phone',
      valueFormatter: (value: string | null) => formatPatientPhone(value),
    },
  ];
}

function getAttendanceHistoryColumn<
  TRow extends GridValidRowModel & WorklistPatientRow,
>(): GridColDef<TRow> {
  return {
    field: 'attendanceHistory',
    headerName: 'No-shows / late cancels',
    sortable: false,
    valueGetter: (_value, row) => formatAttendanceHistory(row.riskFactors),
  };
}

const unconfirmedRecipientColumns: GridColDef<UnconfirmedRow>[] = [
  {
    field: 'patientName',
    headerName: 'Patient',
    valueGetter: (_value, row) => formatUnconfirmedPatientName(row),
    // Typed params so the link renders `valueGetter`'s output rather than formatting the name a
    // second time — the same reason the risk and status columns below type theirs.
    renderCell: (params: GridRenderCellParams<UnconfirmedRow, string>) => (
      <PortalEntityLink entity="patient" id={params.row.patientId}>
        {params.value}
      </PortalEntityLink>
    ),
  },
  ...getPatientContactColumns<UnconfirmedRow>(),
  {
    field: 'appointmentDate',
    headerName: 'Appointment',
    valueGetter: (_value, row) => new Date(row.appointmentStartsAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'appointmentTypeName',
    headerName: 'Appointment type',
  },
  {
    field: 'providerName',
    headerName: 'Provider',
    // Blank provider names exist upstream; the placeholder matches how blank patient names render.
    valueGetter: (_value, row) => row.providerName || emptyCellPlaceholder,
    renderCell: (params: GridRenderCellParams<UnconfirmedRow, string>) => (
      <PortalEntityLink entity="provider" id={params.row.providerId}>
        {params.value}
      </PortalEntityLink>
    ),
  },
  getAttendanceHistoryColumn<UnconfirmedRow>(),
  {
    field: 'riskProbability',
    headerName: 'Attendance risk',
    valueGetter: (_value, row) => getUnconfirmedRowRiskSortValue(row),
    valueFormatter: (_value, row) => formatRiskProbability(getUnconfirmedRowRiskProbability(row)),
    renderCell: (
      // Same reason as the status column below: type the param so the cell renders
      // `valueFormatter`'s output rather than formatting the probability a second time.
      params: GridRenderCellParams<UnconfirmedRow, number, string>
    ) => (
      <Typography
        variant="inherit"
        color={
          getUnconfirmedRowRiskProbability(params.row) === null ? 'text.secondary' : 'text.primary'
        }
      >
        {params.formattedValue}
      </Typography>
    ),
  },
];

const unconfirmedBaseColumns: GridColDef<UnconfirmedRow>[] = [
  ...unconfirmedRecipientColumns,
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 200,
    valueFormatter: (_value, row) => getConfirmationStatusLabel(row),
    renderCell: (
      // The array's `GridColDef<UnconfirmedRow>` context leaves the value generics at `any`, so
      // type the param to consume `valueFormatter`'s output instead of re-deriving the label.
      params: GridRenderCellParams<
        UnconfirmedRow,
        UnconfirmedRowStatus,
        ReturnType<typeof getConfirmationStatusLabel>
      >
    ) =>
      params.row.status === 'not_contacted' && !params.row.outreach ? (
        <Typography variant="inherit" color="text.secondary">
          {params.formattedValue}
        </Typography>
      ) : (
        <Chip label={params.formattedValue} size="small" variant="outlined" />
      ),
  },
];

function UnconfirmedDetailsPanel(props: {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  rows: UnconfirmedRow[];
}) {
  const detailsColumns = React.useMemo<GridColDef<UnconfirmedRow>[]>(
    () => [
      ...unconfirmedBaseColumns,
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 180,
        // Always rendered so contacted rows don't leave a gap in the column — just disabled
        // once the Status cell reflects the action already taken.
        renderCell: (params) => (
          <ReadinessCtaButton
            me={props.me}
            filters={props.filters}
            label="Review Outreach"
            rows={[params.row]}
            color="error"
            size="small"
            actionScope="row"
            // Only ever mounted while this card (unconfirmed) is the selected bucket — the
            // details panel that renders this grid is itself gated on that selection.
            isCardSelected
            disabled={!isSmsReachableUnconfirmedRow(params.row)}
          />
        ),
      },
    ],
    [props.me, props.filters]
  );

  return (
    <Stack direction="column" spacing={2} minHeight={0}>
      <ReadinessDataGrid
        rows={props.rows}
        columns={detailsColumns}
        getRowId={getAppointmentRowId}
        initialState={unconfirmedDataGridInitialState}
        csvFileName="unconfirmed"
        color="error"
        variant="details"
      />
    </Stack>
  );
}

const rebookingEventLabels: Record<RebookingEventKind, string> = {
  no_show: 'No show',
  late_cancel: 'Late cancel',
  canceled: 'Canceled',
};

/**
 * One derivation of the reason copy for both the exported string and the rendered cell, so the two
 * can't disagree on the wording or on which kinds carry a cancellation date. A no-show was never
 * canceled, so it has no date to report.
 */
function getRebookingReasonDetails(row: RescheduleRow): {
  label: string;
  canceledDetail: string | null;
} {
  return {
    label: rebookingEventLabels[row.eventKind],
    canceledDetail:
      row.eventKind === 'no_show' ? null : `Canceled ${formatReadinessDateTime(row.eventAt)}`,
  };
}

function formatRebookingReason(row: RescheduleRow): string {
  const details = getRebookingReasonDetails(row);
  return details.canceledDetail ? `${details.label} - ${details.canceledDetail}` : details.label;
}

function RebookingReasonCell(props: { row: RescheduleRow }) {
  const details = getRebookingReasonDetails(props.row);

  return (
    <Stack direction="column" spacing={0} justifyContent="center">
      <Typography variant="inherit">{details.label}</Typography>
      {details.canceledDetail ? (
        <Typography variant="caption" color="text.secondary">
          {details.canceledDetail}
        </Typography>
      ) : null}
    </Stack>
  );
}

const rescheduleBaseColumns: GridColDef<RescheduleRow>[] = [
  {
    field: 'patientName',
    headerName: 'Patient',
    valueGetter: (_value, row) => formatPersonName(row.patient),
    renderCell: (params: GridRenderCellParams<RescheduleRow, string>) => (
      <PortalEntityLink entity="patient" id={params.row.patient.userId}>
        {params.value}
      </PortalEntityLink>
    ),
  },
  ...getPatientContactColumns<RescheduleRow>(),
  {
    field: 'appointmentDate',
    headerName: 'Missed appointment',
    valueGetter: (_value, row) => new Date(row.startDate),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'appointmentType',
    headerName: 'Appointment type',
    valueGetter: (_value, row) => row.appointmentType.name,
  },
  {
    field: 'providerName',
    headerName: 'Provider',
    valueGetter: (_value, row) => formatPersonName(row.provider),
    renderCell: (params: GridRenderCellParams<RescheduleRow, string>) => (
      <PortalEntityLink entity="provider" id={params.row.provider.userId}>
        {params.value}
      </PortalEntityLink>
    ),
  },
  {
    field: 'reason',
    headerName: 'Reason',
    minWidth: 180,
    valueGetter: (_value, row) => formatRebookingReason(row),
    renderCell: (params) => <RebookingReasonCell row={params.row} />,
  },
  getAttendanceHistoryColumn<RescheduleRow>(),
];

function getBulkRebookingMessageTemplate(
  org: Pick<Org, 'name' | 'formattedPhone'>,
  messageAuthorName: string
): string {
  const callToAction = org.formattedPhone
    ? `\n\nYou can also call the office at ${org.formattedPhone}.`
    : '';

  return `${getBulkMessageIntroduction(messageAuthorName, org.name)} It looks like your last appointment time didn't work for you. Would you like me to add you to [Provider]'s waitlist in case something opens up?${callToAction}`;
}

function notifyAskAboutRebookingStarted(response: CreateOutreachRunResponse) {
  enqueueSnackbar(
    `Rebooking outreach started for ${pluralize(response.targetCount, 'appointment')}.`,
    { variant: 'success' }
  );
}

function notifySingleRebookingOutcome(response: SendPreparedResponse) {
  if (response.outcome === 'sent') {
    enqueueSnackbar('Sent rebooking outreach to 1 patient.', { variant: 'success' });
    return;
  }

  enqueueSnackbar('1 rebooking outreach message could not be sent.', { variant: 'warning' });
}

interface RebookingCtaButtonCommonProps {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  messageAuthorName: string;
  label: string;
  disabled?: boolean;
}

interface RebookingCardCtaButtonProps extends RebookingCtaButtonCommonProps {
  actionScope: 'card';
  outreach: RebookingOutreachSelection;
  isCardSelected: boolean;
  onSelectCard: () => void;
}

interface RebookingRowCtaButtonProps extends RebookingCtaButtonCommonProps {
  actionScope: 'row';
  row: RescheduleRow;
}

type RebookingCtaButtonProps = RebookingCardCtaButtonProps | RebookingRowCtaButtonProps;

function RebookingCtaButton(props: RebookingCtaButtonProps) {
  const queryClient = useQueryClient();
  const readinessQuery = activeReadinessQuery(props.me.org.id, props.filters);
  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const actionableRows =
    props.actionScope === 'card'
      ? props.outreach.rows
      : [props.row].filter(isSmsReachableRebookingRow);
  const excludedAppointmentCount =
    props.actionScope === 'card'
      ? props.outreach.excludedAppointmentCount
      : 1 - actionableRows.length;
  const soleRow = actionableRows[0];

  const askAboutRebookingMutation = useMutation({
    mutationFn: askAboutRebooking,
    onSuccess: (response) => {
      notifyAskAboutRebookingStarted(response);
      void queryClient.invalidateQueries({ queryKey: readinessQuery.queryKey });
      setDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Could not send rebooking outreach. Please try again later.', {
        variant: 'error',
      });
    },
  });

  // Single-appointment rebooking dispatches the exact copy the dialog previewed via `sendPrepared`,
  // rather than routing through the bulk `/mia/rebooking/ask` path that regenerates copy per
  // recipient server-side. Reached only from a row action; card actions always use the bulk path.
  const sendPreparedMutation = useMutation({
    mutationFn: (request: SendPreparedRequest) => sendPrepared(request),
    onSuccess: (response) => {
      notifySingleRebookingOutcome(response);

      // A skipped/failed outcome is terminal for this send (invalid address, missing recipient,
      // dispatch failure) — leave the dialog open with the warning so Cancel is the exit.
      if (response.outcome !== 'sent' || !soleRow) {
        return;
      }

      const sentAppointmentId = soleRow.id;

      queryClient.setQueryData<PatientReadinessDetailsResponse>(
        readinessQuery.queryKey,
        (currentData) => {
          if (!currentData) {
            return currentData;
          }

          // A verbatim single send only touches one appointment — patch just that row to
          // `contacted` instead of refetching the whole worklist, matching the bulk path's
          // per-sent-row patch.
          return {
            ...currentData,
            reschedule: currentData.reschedule.map((row) =>
              row.id === sentAppointmentId ? { ...row, status: 'contacted' as const } : row
            ),
          };
        }
      );

      setDialogOpen(false);
    },
    onError: () => {
      enqueueSnackbar('Could not send rebooking outreach. Please try again later.', {
        variant: 'error',
      });
    },
  });

  function openDialog() {
    if (props.actionScope === 'card' && !props.isCardSelected) {
      props.onSelectCard();
      return;
    }

    if (actionableRows.length === 0) {
      enqueueSnackbar('There are no patients to contact about rebooking.', {
        variant: 'error',
      });
      return;
    }

    trackPendoEvent('coordinate_rebooking_dialog_opened', {
      bucket: 'reschedule',
      appointment_count: actionableRows.length,
      excluded_count: excludedAppointmentCount,
      action_scope: props.actionScope,
    });
    setDialogOpen(true);
  }

  function confirmAndSend() {
    const request = {
      orgId: props.me.org.id,
      appointmentIds: actionableRows.map((row) => row.id),
    };
    trackPendoEvent('coordinate_rebooking_outreach_sent', {
      bucket: 'reschedule',
      appointment_count: request.appointmentIds.length,
      excluded_count: excludedAppointmentCount,
      action_scope: props.actionScope,
    });
    askAboutRebookingMutation.mutate(request);
  }

  function confirmSinglePrepared(prepared: PreparedMessage) {
    if (!soleRow) {
      return;
    }

    trackPendoEvent('coordinate_rebooking_outreach_sent', {
      bucket: 'reschedule',
      appointment_count: 1,
      action_scope: props.actionScope,
    });
    sendPreparedMutation.mutate({
      link: { topic: 'rebooking', appointmentId: soleRow.id },
      channel: prepared.channel,
      body: prepared.body,
      subject: prepared.subject,
    });
  }

  return (
    <>
      <Button
        variant={props.actionScope === 'card' ? 'contained' : 'outlined'}
        size={props.actionScope === 'card' ? 'medium' : 'small'}
        startIcon={<FontAwesomeScalableIcon icon={faCalendarPlus} />}
        disabled={props.disabled}
        color="warning"
        aria-label={
          props.actionScope === 'row' && soleRow
            ? `${props.label} for ${formatPersonName(soleRow.patient)}`
            : props.actionScope === 'card'
              ? `${props.label} for rebooking`
              : undefined
        }
        onClick={openDialog}
      >
        {props.label}
      </Button>

      {isDialogOpen && soleRow ? (
        props.actionScope === 'card' ? (
          <RebookDialog
            isBulk
            onClose={() => setDialogOpen(false)}
            onConfirm={confirmAndSend}
            status={askAboutRebookingMutation.isPending ? 'submitting' : 'idle'}
            confirmLabel={getSendMessagesConfirmLabel(actionableRows.length)}
            template={getBulkRebookingMessageTemplate(props.me.org, props.messageAuthorName)}
            recipientCount={props.outreach.recipientCount}
            excludedPatientCount={props.outreach.excludedPatientCount}
          >
            <ReadinessDataGrid
              rows={actionableRows}
              columns={rescheduleBaseColumns}
              variant="dialog"
            />
          </RebookDialog>
        ) : (
          <SingleRebookDialog
            row={soleRow}
            onClose={() => setDialogOpen(false)}
            onConfirm={confirmSinglePrepared}
            isSending={sendPreparedMutation.isPending}
          />
        )
      ) : null}
    </>
  );
}

function mapRebookingRowToDialogRecipient(row: RescheduleRow) {
  return {
    id: row.id,
    name: formatPersonName(row.patient),
    appointmentLabel: formatReadinessDateTime(row.startDate),
    providerName: formatPersonName(row.provider),
  };
}

/**
 * Single-appointment rebooking dialog: previews the actual copy Mia would send (generated
 * server-side) and dispatches it verbatim. This UI keeps rebooking SMS-only until backend channel
 * execution availability is exposed. Mounted only while open so the preview request fires exactly
 * when the dialog appears.
 */
function SingleRebookDialog(props: {
  row: SmsReachableRow<RescheduleRow>;
  onClose: () => void;
  onConfirm: (prepared: PreparedMessage) => void;
  isSending: boolean;
}) {
  const channel: MiaMessageChannel = 'sms';

  const previewQuery = useQuery(
    miaMessagePreview({ kind: 'rebooking', appointmentId: props.row.id, channel })
  );

  // Confirm dispatches the previewed copy verbatim. The button is only enabled once the preview
  // succeeds, so `previewQuery.data` is present here; the server-resolved channel is used.
  function confirmPrepared() {
    if (!previewQuery.isSuccess) {
      return;
    }

    props.onConfirm({
      body: previewQuery.data.body,
      subject: previewQuery.data.subject,
      channel: previewQuery.data.channel,
    });
  }

  // Staff send exactly the copy they previewed, so a failed preview blocks the send (the dialog's
  // `error` status disables confirming) rather than falling back to copy Mia may not actually write.
  const body = previewQuery.isPending ? (
    <Skeleton data-testid="rebooking-message-skeleton" />
  ) : previewQuery.isError ? (
    <Typography component="span" variant="inherit" color="error.main">
      Message preview could not be loaded. Please try again later.
    </Typography>
  ) : (
    previewQuery.data.body
  );

  return (
    <RebookDialog
      onClose={props.onClose}
      onConfirm={confirmPrepared}
      status={
        props.isSending
          ? 'submitting'
          : previewQuery.isPending
            ? 'loading'
            : previewQuery.isError
              ? 'error'
              : 'idle'
      }
      confirmLabel={getSendMessagesConfirmLabel(1)}
      recipient={mapRebookingRowToDialogRecipient(props.row)}
      preview={{
        // Label the channel the preview actually resolved to, not the requested `channel` const —
        // the send dispatches `previewQuery.data.channel`, so the label must track the same source.
        channel: previewQuery.data?.channel ?? channel,
        body,
        subject: previewQuery.data?.subject,
      }}
    />
  );
}

const rebookingStatusDetails: Record<RebookingCandidateStatus, { label: string }> = {
  needs_outreach: { label: needsOutreachStatusLabel },
  contacted: { label: 'Contacted' },
  waitlisted: { label: 'On waitlist' },
  staff_follow_up: { label: 'Staff follow-up' },
};

function getRebookingStatusLabel(row: RescheduleRow): string {
  if (row.status !== 'needs_outreach') {
    return rebookingStatusDetails[row.status].label;
  }

  if (row.outreach) {
    return getOutreachStatusLabel(row.outreach);
  }

  return row.preferredChannel === 'sms' ? needsOutreachStatusLabel : patientUnreachableStatusLabel;
}

function RebookingStatusCell(props: { row: RescheduleRow }) {
  return <Chip label={getRebookingStatusLabel(props.row)} size="small" variant="outlined" />;
}

function RebookingActionCell(props: {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  messageAuthorName: string;
  row: RescheduleRow;
}) {
  if (props.row.preferredChannel !== 'sms') {
    return (
      <Typography variant="inherit" color="text.secondary">
        No mobile number
      </Typography>
    );
  }

  return (
    <RebookingCtaButton
      me={props.me}
      filters={props.filters}
      messageAuthorName={props.messageAuthorName}
      label="Review outreach"
      actionScope="row"
      row={props.row}
      disabled={props.row.status !== 'needs_outreach' || !canStartOutreach(props.row.outreach)}
    />
  );
}

function RescheduleDetailsPanel(props: {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  messageAuthorName: string;
  rows: RescheduleRow[];
}) {
  const detailsColumns = React.useMemo<GridColDef<RescheduleRow>[]>(
    () => [
      ...rescheduleBaseColumns,
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 160,
        valueGetter: (_value, row) => getRebookingStatusLabel(row),
        renderCell: (params) => <RebookingStatusCell row={params.row} />,
      },
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 200,
        renderCell: (params) => (
          <RebookingActionCell
            me={props.me}
            filters={props.filters}
            messageAuthorName={props.messageAuthorName}
            row={params.row}
          />
        ),
      },
    ],
    [props.me, props.filters, props.messageAuthorName]
  );

  return (
    <Stack direction="column" spacing={2} minHeight={0}>
      <ReadinessDataGrid
        rows={props.rows}
        columns={detailsColumns}
        initialState={rescheduleDataGridInitialState}
        csvFileName="reschedule"
        color="warning"
        variant="details"
      />
    </Stack>
  );
}

/** The claimed offer's patient name — the winner a staff Approve would book. */
function getSlotClaimantName(slotFillState: NonNullable<OfferableSlot['slotFillState']>): string {
  const claimedOffer = slotFillState.offers.find((offer) => offer.status === 'claimed');
  return formatPersonName({
    firstName: claimedOffer?.patientFirstName,
    lastName: claimedOffer?.patientLastName,
  });
}

function getSlotFillStatusLabel(slot: OfferableSlot): string {
  if (!slot.slotFillState) {
    return slot.outreach ? getOutreachStatusLabel(slot.outreach) : needsOutreachStatusLabel;
  }

  if (slot.slotFillState.status === 'claimed') {
    return `Claimed by ${getSlotClaimantName(slot.slotFillState)}`;
  }

  if (slot.slotFillState.status === 'filled') {
    return 'Booked';
  }

  const offerCount = slot.slotFillState.offers.length;
  return `Offered to ${offerCount} ${offerCount === 1 ? 'patient' : 'patients'}`;
}

/**
 * The provider column is presented identically by the offerable-slots feed grid and by the bulk
 * offer dialog's review table — one definition, so what staff review can't drift from the row
 * they clicked. Generic over the row type because the dialog widens it.
 */
function getOfferableSlotProviderColumn<
  TRow extends GridValidRowModel &
    Pick<OfferableSlot, 'providerId' | 'providerFirstName' | 'providerLastName'>,
>(): GridColDef<TRow> {
  return {
    field: 'providerName',
    headerName: 'Provider',
    valueGetter: (_value, row) => formatSlotProviderName(row),
    renderCell: (params: GridRenderCellParams<TRow, string>) => (
      <PortalEntityLink entity="provider" id={params.row.providerId}>
        {params.value}
      </PortalEntityLink>
    ),
  };
}

const offerableSlotBaseColumns: GridColDef<OfferableSlot>[] = [
  getOfferableSlotProviderColumn<OfferableSlot>(),
  {
    field: 'canceledAt',
    headerName: 'Canceled',
    valueGetter: (_value, row) => new Date(row.canceledAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  {
    field: 'appointmentTime',
    headerName: 'Appointment time',
    valueGetter: (_value, row) => new Date(row.slotStartsAt),
    valueFormatter: (value: Date, row) => formatReadinessTimeRange(value, new Date(row.slotEndsAt)),
  },
  {
    field: 'candidateReach',
    headerName: 'Waitlist candidates',
    valueGetter: (_value, row) => row.candidates.length,
    valueFormatter: (value: number) => pluralize(value, 'candidate'),
    renderCell: (params: GridRenderCellParams<OfferableSlot, number, string>) => (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <FontAwesomeScalableIcon icon={faUserGroup} fontSize="small" />
        <Typography variant="inherit">{params.formattedValue}</Typography>
      </Stack>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 200,
    valueGetter: (_value, row) => getSlotFillStatusLabel(row),
    renderCell: (params) => <SlotFillStatusCell slot={params.row} />,
  },
];

function notifyOfferSlotStarted(response: CreateOutreachRunResponse) {
  enqueueSnackbar(`Waitlist outreach started for ${pluralize(response.targetCount, 'slot')}.`, {
    variant: 'success',
  });
}

/** A status chip whose tooltip (and accessible label) lists everyone the slot was offered to. */
function OffereeTooltipChip(props: {
  label: string;
  slotFillState: NonNullable<OfferableSlot['slotFillState']>;
}) {
  const offereeNames = props.slotFillState.offers
    .map((offer) =>
      formatPersonName({ firstName: offer.patientFirstName, lastName: offer.patientLastName })
    )
    .join(', ');

  return (
    <Tooltip title={offereeNames || 'No offerees'}>
      {/* Chip without onClick isn't focusable, which would leave the tooltip
          mouse-only — tabIndex lets keyboard focus surface it, and the
          aria-label carries the offeree names for screen readers directly. */}
      <Chip
        label={props.label}
        size="small"
        variant="outlined"
        tabIndex={0}
        aria-label={`${props.label}: ${offereeNames || 'no offerees'}`}
      />
    </Tooltip>
  );
}

/**
 * The Status cell: what happened to the slot's offer, whoever holds the claim included. The
 * actionable half (offer trigger, approve/reject) lives in the Action column. Triggered slots
 * persist in the feed until their start time passes, so `filled` is the normal terminal state of
 * every approved slot, not a transient.
 */
function SlotFillStatusCell(props: { slot: OfferableSlot }) {
  // The same label the column's `valueGetter` exports, so the cell and the CSV can't word a status
  // differently. Only the treatment branches below it.
  const label = getSlotFillStatusLabel(props.slot);

  const { slotFillState } = props.slot;
  if (!slotFillState) {
    if (props.slot.outreach) {
      return <Chip label={label} size="small" variant="outlined" />;
    }

    return (
      <Typography variant="inherit" color="text.secondary">
        {label}
      </Typography>
    );
  }

  // A claimed slot's chip names the claimant rather than the offerees, so it carries no tooltip.
  if (slotFillState.status === 'claimed') {
    return <Chip label={label} size="small" variant="outlined" />;
  }

  return <OffereeTooltipChip label={label} slotFillState={slotFillState} />;
}

/**
 * The claim queue's inline actions on a claimed slot. Approve books the winner's own waitlisted
 * appointment type server-side and Mia messages the patient — the UI only toasts and refetches.
 * "Reject" is the staff-facing label; the endpoint is named for what it does: release the
 * claim so the slot is back in play. A `booked: false` outcome changes nothing server-side, so
 * the row stays claimed with both actions live for a retry or an explicit release.
 */
function ClaimedSlotActions(props: {
  orgId: number;
  filters: FeedFilters;
  slotFillState: NonNullable<OfferableSlot['slotFillState']>;
}) {
  const queryClient = useQueryClient();
  const slotsQuery = activeOfferableSlotsQuery(props.orgId, props.filters);

  const claimantName = getSlotClaimantName(props.slotFillState);

  function refetchSlots() {
    void queryClient.invalidateQueries({ queryKey: slotsQuery.queryKey });
  }

  const approveMutation = useMutation({
    mutationFn: () => approveSlotFill({ slotFillId: props.slotFillState.slotFillId }),
    onSuccess: (response) => {
      if (!response.booked) {
        // No refetch: the server changed nothing, and the row must stay claimed and actionable.
        enqueueSnackbar(getCouldntBookMessage(response.reason), { variant: 'error' });
        return;
      }

      enqueueSnackbar(`Booked this slot for ${claimantName}.`, { variant: 'success' });
      refetchSlots();
    },
    onError: (error) => {
      // A rejection means the row changed underneath (claim released/expired, org mismatch) —
      // refetch so the row reflects whatever it became.
      enqueueSnackbar(getClaimActionErrorMessage(error), { variant: 'error' });
      refetchSlots();
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () => releaseSlotFillClaim({ slotFillId: props.slotFillState.slotFillId }),
    onSuccess: () => {
      enqueueSnackbar('Claim released — this slot is open to offers again.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getClaimActionErrorMessage(error), { variant: 'error' });
    },
    onSettled: refetchSlots,
  });

  // Both actions lock while either is in flight, and stay locked through the settle-to-refetch
  // window after an outcome that changed the row (a booking or a release) — a second click there
  // would double-book or release a claim that no longer exists. A booked:false outcome leaves the
  // row claimed, so the actions re-enable for a retry.
  const actionsLocked =
    approveMutation.isPending ||
    releaseMutation.isPending ||
    (approveMutation.isSuccess && approveMutation.data.booked) ||
    releaseMutation.isSuccess;

  return (
    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined"
        size="small"
        color="quaternary"
        startIcon={<FontAwesomeScalableIcon icon={faCheck} />}
        loading={approveMutation.isPending}
        loadingPosition="start"
        disabled={actionsLocked}
        // Row-scoped label so the repeated action names the claimant it books (a11y + tests).
        aria-label={`Approve for ${claimantName}`}
        onClick={() => approveMutation.mutate()}
      >
        Approve
      </Button>

      <Button
        variant="outlined"
        size="small"
        startIcon={<FontAwesomeScalableIcon icon={faXmark} />}
        loading={releaseMutation.isPending}
        loadingPosition="start"
        disabled={actionsLocked}
        aria-label={`Reject for ${claimantName}`}
        onClick={() => releaseMutation.mutate()}
      >
        Reject
      </Button>
    </Stack>
  );
}

// An org's offer batch size caps how many waitlist candidates one slot reaches. That cap decides
// the CTA's enabled state, the analytics payload, the dialog's copy and its confirm label — so all
// of them count through here rather than each re-applying the rule.
function countReachableCandidates(slots: OfferableSlot[], offerBatchSize: number): number {
  return slots.reduce(
    (count, slot) => count + getReachableSlotCandidates(slot, offerBatchSize).length,
    0
  );
}

interface OfferSlotsActionCommonProps {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  offerBatchSize: number;
  label: string;
  disabled?: boolean;
}

interface OfferSlotsCardActionProps extends OfferSlotsActionCommonProps {
  actionScope: 'card';
  slots: OfferableSlot[];
  totalSlotCount: number;
  isCardSelected: boolean;
  onSelectCard: () => void;
}

interface OfferSlotsRowActionProps extends OfferSlotsActionCommonProps {
  actionScope: 'row';
  slot: OfferableSlot;
}

type OfferSlotsActionProps = OfferSlotsCardActionProps | OfferSlotsRowActionProps;

/**
 * What staff are reviewing, frozen when they opened the dialog: the slots, and the filters the
 * offer is constrained by. The slots feed polls, so the send has to dispatch what was reviewed
 * rather than whatever has landed since. The mode is carried rather than inferred so neither the
 * dialog nor the send has to treat "the single slot" as element zero of an array.
 */
type SlotOfferReview = { filters: FeedFilters } & (
  | { mode: 'single'; slot: OfferableSlot }
  | { mode: 'bulk'; slots: OfferableSlot[]; excludedSlotCount: number }
);

function getReviewedSlots(review: SlotOfferReview): OfferableSlot[] {
  return review.mode === 'single' ? [review.slot] : review.slots;
}

function isSlotOfferReviewActionable({
  review,
  currentSlots,
  offerBatchSize,
}: {
  review: SlotOfferReview;
  currentSlots: OfferableSlot[];
  offerBatchSize: number;
}): boolean {
  const currentSlotsById = new Map(currentSlots.map((slot) => [slot.appointmentId, slot]));
  return getReviewedSlots(review).every((reviewedSlot) => {
    const currentSlot = currentSlotsById.get(reviewedSlot.appointmentId);
    return (
      currentSlot !== undefined &&
      !currentSlot.slotFillState &&
      getReachableSlotCandidates(currentSlot, offerBatchSize).length > 0 &&
      canStartOutreach(currentSlot.outreach)
    );
  });
}

function OfferSlotsAction(props: OfferSlotsActionProps) {
  const queryClient = useQueryClient();
  const slotsQuery = activeOfferableSlotsQuery(props.me.org.id, props.filters);
  const [review, setReview] = React.useState<SlotOfferReview | null>(null);
  const cardSlots = props.actionScope === 'card' ? props.slots : undefined;
  const rowSlot = props.actionScope === 'row' ? props.slot : undefined;
  const slots = cardSlots ?? (rowSlot ? [rowSlot] : []);

  React.useEffect(() => {
    const currentSlots = cardSlots ?? (rowSlot ? [rowSlot] : []);
    if (
      !review ||
      isSlotOfferReviewActionable({
        review,
        currentSlots,
        offerBatchSize: props.offerBatchSize,
      })
    ) {
      return;
    }

    enqueueSnackbar('This outreach changed while you were reviewing it. Please review it again.', {
      variant: 'warning',
    });
    setReview(null);
  }, [cardSlots, props.offerBatchSize, review, rowSlot]);

  const offerMutation = useMutation({
    mutationFn: (request: OfferSlotToWaitlistRequest) => offerSlotToWaitlist(request),
    onSuccess: (response) => {
      notifyOfferSlotStarted(response);
      setReview(null);
    },
    onError: (error) => {
      enqueueSnackbar(getOfferSlotErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: slotsQuery.queryKey }),
  });

  if (props.actionScope === 'row' && props.slot.slotFillState?.status === 'claimed') {
    return (
      <ClaimedSlotActions
        orgId={props.me.org.id}
        filters={props.filters}
        slotFillState={props.slot.slotFillState}
      />
    );
  }

  const recipientCount = countReachableCandidates(slots, props.offerBatchSize);

  // No "nothing to contact" guard here, unlike the confirmation and rebooking CTAs: this button
  // disables itself on an empty candidate count, so the disabled state is the whole story.
  function openDialog() {
    if (props.actionScope === 'card' && !props.isCardSelected) {
      props.onSelectCard();
      return;
    }

    trackPendoEvent('coordinate_slot_offer_dialog_opened', {
      bucket: 'waitlist',
      appointment_count: slots.length,
      candidate_count: recipientCount,
      action_scope: props.actionScope,
    });
    setReview(
      props.actionScope === 'card'
        ? {
            mode: 'bulk',
            slots: props.slots,
            excludedSlotCount: props.totalSlotCount - props.slots.length,
            filters: props.filters,
          }
        : { mode: 'single', slot: props.slot, filters: props.filters }
    );
  }

  function confirmAndSend() {
    if (!review) {
      return;
    }

    const reviewedSlots = getReviewedSlots(review);
    trackPendoEvent('coordinate_slot_offers_sent', {
      bucket: 'waitlist',
      appointment_count: reviewedSlots.length,
      candidate_count: countReachableCandidates(reviewedSlots, props.offerBatchSize),
      action_scope: props.actionScope,
    });
    offerMutation.mutate({
      orgId: props.me.org.id,
      appointmentIds: reviewedSlots.map((slot) => slot.appointmentId),
      appointmentTypeIds: review.filters.appointmentTypeIds,
      baseAppointmentTypes: review.filters.baseAppointmentTypes,
    });
  }

  return (
    <>
      <Button
        variant={props.actionScope === 'card' ? 'contained' : 'outlined'}
        size={props.actionScope === 'card' ? 'medium' : 'small'}
        color="quaternary"
        startIcon={<FontAwesomeScalableIcon icon={faPaperPlane} />}
        disabled={props.disabled || recipientCount === 0 || offerMutation.isPending}
        aria-label={
          props.actionScope === 'row'
            ? `${props.label} for ${formatSlotProviderName(props.slot)} on ${formatReadinessDateTime(props.slot.slotStartsAt)}`
            : `${props.label} for waitlist slot offers`
        }
        onClick={openDialog}
      >
        {props.label}
      </Button>

      {review ? (
        <SlotOfferDialog
          review={review}
          org={props.me.org}
          offerBatchSize={props.offerBatchSize}
          onClose={() => setReview(null)}
          onConfirm={confirmAndSend}
          isSubmitting={offerMutation.isPending}
        />
      ) : null}
    </>
  );
}

interface SlotOfferDialogRow extends OfferableSlot {
  reachableCandidateCount: number;
}

const slotOfferDialogColumns: GridColDef<SlotOfferDialogRow>[] = [
  {
    // Start time only, unlike the feed's range column: the group's message quotes the slot start,
    // so the review table lists the same value.
    field: 'appointmentTime',
    headerName: 'Open slot',
    valueGetter: (_value, row) => new Date(row.slotStartsAt),
    valueFormatter: (value: Date) => formatReadinessDateTime(value),
  },
  getOfferableSlotProviderColumn<SlotOfferDialogRow>(),
  {
    field: 'reachableCandidateCount',
    headerName: 'Group size',
    renderCell: (params: GridRenderCellParams<SlotOfferDialogRow, number>) => (
      <Chip
        size="small"
        icon={<FontAwesomeScalableIcon icon={faUserGroup} fontSize="inherit" />}
        label={params.value}
      />
    ),
  },
];

type SlotOfferCandidate = OfferableSlot['candidates'][number];

// The slot's type and provider are not repeated per candidate row — the info items above the
// message already carry them for the whole group.
const slotOfferCandidateColumns: GridColDef<SlotOfferCandidate>[] = [
  {
    field: 'patientName',
    headerName: 'Patient',
    valueGetter: (_value, row) =>
      formatPersonName({
        firstName: row.patientFirstName,
        lastName: row.patientLastName,
      }),
    renderCell: (params: GridRenderCellParams<SlotOfferCandidate, string>) => (
      <PortalEntityLink entity="patient" id={params.row.patientId}>
        {params.value}
      </PortalEntityLink>
    ),
  },
  {
    field: 'patientAge',
    headerName: 'Age',
    valueGetter: (_value, row) => getPatientAge(row.patientBirthDate),
    valueFormatter: (value: number | null) => formatPatientAge(value),
  },
  {
    field: 'patientPhone',
    headerName: 'Phone',
    valueFormatter: (value: string | null) => formatPatientPhone(value),
  },
  {
    field: 'waitlistedAt',
    headerName: 'On waitlist',
    valueGetter: (_value, row) => differenceInCalendarDays(new Date(), new Date(row.waitlistedAt)),
    valueFormatter: (value: number) =>
      value < 1 ? 'Waiting < 1 day' : `Waiting ${pluralize(value, 'day')}`,
  },
];

function buildSlotOfferMessage(params: {
  appointmentPhrase: string;
  providerName: string;
  slotStartLabel: string;
  officePhone: Org['formattedPhone'];
}): string {
  // Same fallback shape as the rebooking template: name the office number when the org has one.
  const claimCallToAction = params.officePhone
    ? `reply YES or call ${params.officePhone} to claim it`
    : 'reply YES or call the office to claim it';

  return `Hi [Patient Name], ${params.appointmentPhrase} just opened with ${params.providerName} on ${params.slotStartLabel}. You asked to be seen sooner — ${claimCallToAction}. The first person to respond gets the slot.`;
}

function getSlotOfferMessage(org: Pick<Org, 'formattedPhone'>, slot: OfferableSlot): string {
  return buildSlotOfferMessage({
    // Type names have no length floor — a blank one drops the type phrase rather than rendering
    // "an earlier  appointment".
    appointmentPhrase: slot.appointmentTypeName
      ? `an earlier ${slot.appointmentTypeName} appointment`
      : 'an earlier appointment',
    providerName: formatSlotProviderName(slot),
    slotStartLabel: formatReadinessDateTime(slot.slotStartsAt),
    officePhone: org.formattedPhone,
  });
}

// The bulk preview is the same sentence with the per-group details left as the bracketed
// placeholders the rest of the readiness templates use; the appointment phrase stays generic
// because each group's type differs.
function getSlotOfferMessageTemplate(org: Pick<Org, 'formattedPhone'>): string {
  return buildSlotOfferMessage({
    appointmentPhrase: 'an earlier appointment',
    providerName: '[Provider]',
    slotStartLabel: '[Slot start]',
    officePhone: org.formattedPhone,
  });
}

function toSlotOfferDialogRows(
  slots: OfferableSlot[],
  offerBatchSize: number
): SlotOfferDialogRow[] {
  return slots.map((slot) => ({
    ...slot,
    reachableCandidateCount: getReachableSlotCandidates(slot, offerBatchSize).length,
  }));
}

function getReachableSlotCandidates(
  slot: OfferableSlot,
  offerBatchSize: number
): SlotOfferCandidate[] {
  return slot.candidates
    .slice(0, offerBatchSize)
    .filter((candidate) => candidate.preferredChannel === 'sms');
}

function getBulkSlotOfferNote(excludedSlotCount: number): string | null {
  if (excludedSlotCount === 0) {
    return null;
  }

  return `${excludedSlotCount} ${excludedSlotCount === 1 ? 'slot is' : 'slots are'} excluded because outreach was already sent or no candidates are reachable.`;
}

function SlotOfferDialog(props: {
  review: SlotOfferReview;
  org: Pick<Org, 'formattedPhone'>;
  offerBatchSize: number;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}) {
  const reviewedSlots = getReviewedSlots(props.review);
  const recipientCount = countReachableCandidates(reviewedSlots, props.offerBatchSize);

  return (
    <OutreachDialog.Root onClose={props.onClose} maxWidth="lg">
      <OutreachDialog.Header color="quaternary" icon={faUserGroup}>
        {props.review.mode === 'bulk'
          ? `Review waitlist outreach for ${pluralize(props.review.slots.length, 'open slot')}`
          : 'Review waitlist outreach'}
      </OutreachDialog.Header>

      <OutreachDialog.Content>
        {props.review.mode === 'bulk' ? (
          <Typography component="p" variant="body2" marginBottom={2}>
            I&apos;ve prepared outreach for each open slot, sent to the group of waitlisted patients
            who are candidates for it. The first to respond claims the slot. Review it, then send.
          </Typography>
        ) : (
          <Stack direction="row" spacing={2} mb={2}>
            <OutreachDialog.InfoItem
              label="Provider"
              value={formatSlotProviderName(props.review.slot)}
            />
            <OutreachDialog.InfoItem
              label="Open slot"
              value={formatReadinessTimeRange(
                new Date(props.review.slot.slotStartsAt),
                new Date(props.review.slot.slotEndsAt)
              )}
            />
            <OutreachDialog.InfoItem
              label="Appointment type"
              value={props.review.slot.appointmentTypeName || emptyCellPlaceholder}
            />
          </Stack>
        )}

        <OutreachDialog.MessagePreview
          label={props.review.mode === 'bulk' ? 'Message to each waitlist group' : 'Message'}
        >
          {props.review.mode === 'bulk'
            ? getSlotOfferMessageTemplate(props.org)
            : getSlotOfferMessage(props.org, props.review.slot)}
        </OutreachDialog.MessagePreview>

        <OutreachDialog.RecipientTableSection
          label={
            props.review.mode === 'bulk'
              ? `Open slots (${props.review.slots.length})`
              : 'Patients in this group'
          }
          note={
            props.review.mode === 'bulk'
              ? getBulkSlotOfferNote(props.review.excludedSlotCount)
              : null
          }
        >
          {props.review.mode === 'bulk' ? (
            <ReadinessDataGrid
              rows={toSlotOfferDialogRows(props.review.slots, props.offerBatchSize)}
              columns={slotOfferDialogColumns}
              getRowId={getAppointmentRowId}
              variant="dialog"
            />
          ) : (
            <ReadinessDataGrid
              rows={getReachableSlotCandidates(props.review.slot, props.offerBatchSize)}
              columns={slotOfferCandidateColumns}
              getRowId={(candidate) => candidate.patientId}
              variant="dialog"
            />
          )}
        </OutreachDialog.RecipientTableSection>

        <OutreachDialog.DeliverySummary>
          {props.review.mode === 'bulk'
            ? `Sent via SMS · ${pluralize(recipientCount, 'patient')} across ${pluralize(reviewedSlots.length, 'waitlist group')} will be messaged.`
            : 'Sent via SMS · first patient to respond claims the slot'}
        </OutreachDialog.DeliverySummary>
      </OutreachDialog.Content>

      <OutreachDialog.Footer
        color="quaternary"
        confirmLabel={
          props.review.mode === 'bulk'
            ? 'Send All Messages'
            : `Send to ${recipientCount} ${recipientCount === 1 ? 'Candidate' : 'Candidates'}`
        }
        icon={faPaperPlane}
        onConfirm={props.onConfirm}
        status={props.isSubmitting ? 'submitting' : 'idle'}
      />
    </OutreachDialog.Root>
  );
}

function OfferableSlotsDetailsPanel(props: {
  me: GetMeResponsePayload;
  filters: FeedFilters;
  offerBatchSize: number;
  slots: OfferableSlot[];
}) {
  const detailsColumns = React.useMemo<GridColDef<OfferableSlot>[]>(
    () => [
      ...offerableSlotBaseColumns,
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 220,
        renderCell: (params) => (
          <OfferSlotsAction
            me={props.me}
            filters={props.filters}
            offerBatchSize={props.offerBatchSize}
            label="Review outreach"
            actionScope="row"
            slot={params.row}
            disabled={Boolean(params.row.slotFillState) || !canStartOutreach(params.row.outreach)}
          />
        ),
      },
    ],
    [props.me, props.filters, props.offerBatchSize]
  );

  return (
    <Stack direction="column" spacing={2} minHeight={0}>
      <ReadinessDataGrid
        rows={props.slots}
        columns={detailsColumns}
        getRowId={getAppointmentRowId}
        initialState={offerableSlotsDataGridInitialState}
        csvFileName="offerable-slots"
        color="quaternary"
        variant="details"
      />
    </Stack>
  );
}

function SlotFillUnavailableDetails() {
  return (
    <Card sx={{ padding: 2, bgcolor: 'background.default', backgroundImage: 'none' }}>
      <Typography component="p" variant="body1" color="text.secondary" textAlign="center">
        Appointment slot filling isn&apos;t enabled for this organization.
      </Typography>
    </Card>
  );
}

function AutomaticOutreachEnableDialog(props: {
  workflow: AutomaticOutreachWorkflow;
  localHour: number;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const localTime = format(new Date(2000, 0, 1, props.localHour), 'h:mm a');
  const presentation = automaticOutreachWorkflowPresentation[props.workflow];

  return (
    <Dialog.Root
      open
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <Dialog.Header>
        <Dialog.Title id={titleId}>Turn on automatic {presentation.label} outreach?</Dialog.Title>
        <Dialog.Description id={descriptionId} variant="body2">
          {presentation.getScheduleDescription(localTime)} Turning it off later stops future
          automatic runs without interrupting outreach already in progress.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Footer>
        <Dialog.DismissButton disabled={props.isSubmitting}>Cancel</Dialog.DismissButton>
        <Button
          variant="contained"
          size="large"
          onClick={props.onConfirm}
          disabled={props.isSubmitting}
        >
          Turn on
        </Button>
      </Dialog.Footer>
    </Dialog.Root>
  );
}
